import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import SettingsSuggestOutlinedIcon from "@mui/icons-material/SettingsSuggestOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import SpaIcon from "@mui/icons-material/Spa";
import HotelOutlinedIcon from "@mui/icons-material/HotelOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import SidebarLayout from "../components/sidebarLayout";
import Autocomplete from "../components/SearchableAutocomplete";
import SpaGroupBookingDialog from "../components/SpaGroupBookingDialog";
import SpaCancellationPolicyEditor from "../components/SpaCancellationPolicyEditor";
import SpaAppointmentCommunications from "../components/SpaAppointmentCommunications";
import {
  changeSpaAppointmentCommercialStatus,
  checkSpaAppointmentAvailability,
  checkoutSpaAppointment,
  createSpaCabin,
  createOrLinkSpaAppointmentOrder,
  createSpaOperatorInvitation,
  createSpaPlannerNote,
  createSpaAppointment,
  dateTimeWithLocalOffset,
  DEFAULT_SPA_SCHEDULING_SETTINGS,
  deleteSpaPlannerNote,
  getSpaAppointmentPmsCharges,
  getSpaAppointment,
  getSpaCabins,
  getSpaAppointmentOrder,
  getSpaEquipment,
  getSpaCancellationFee,
  getSpaCancellationPolicy,
  getSpaOperatorInvitations,
  getSpaOperators,
  getSpaPlanner,
  getSpaTreatmentAddons,
  getSpaSchedulingSettings,
  getSpaTreatments,
  linkSpaAppointmentPmsReservation,
  postSpaCancellationFee,
  resendSpaOperatorInvitation,
  rescheduleSpaAppointment,
  revokeSpaOperatorInvitation,
  searchSpaPmsReservations,
  SPA_DIRECT_PAYMENT_METHODS,
  SPA_STATUSES,
  updateSpaCancellationPolicy,
  updateSpaAppointmentAddOns,
  updateSpaSchedulingSettings,
} from "../APIs/SpaAPI";
import { getAllTavoli } from "../APIs/TablesAPI";
import { getAllClienti } from "../APIs/ClientsAPI";
import { getAllPackages } from "../APIs/PackagesAPI";
import { useSnackbar } from "../contexts/SnackBarContext";
import { usePermissions } from "../contexts/PermissionsContext";
import SpaSmartAvailability from '../components/SpaSmartAvailability';
import { formatSpaAvailabilityRejection, sameSpaResourceCombination } from '../utils/spaAvailability';
import {
  buildCancellationPolicyPayload,
  cancellationPolicyToDraft,
  defaultCancellationPolicyDraft,
  validateCancellationPolicyDraft,
} from "../utils/spaCancellationPolicy";

const todayKey = () => new Date().toISOString().slice(0, 10);

const addDays = (dateKey, amount) => {
  const date = new Date(`${dateKey}T00:00:00`);
  date.setDate(date.getDate() + amount);
  return date.toISOString().slice(0, 10);
};

const localeByLanguage = {
  en: "en-US",
  it: "it-IT",
  sq: "sq-AL",
};

const formatDayLabel = (dateKey, language = "it") => {
  const date = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateKey;
  return new Intl.DateTimeFormat(localeByLanguage[language] || "it-IT", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  }).format(date);
};

const formatLongDate = (dateKey, language = "it") => {
  const date = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateKey;
  return new Intl.DateTimeFormat(localeByLanguage[language] || "it-IT", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
};

const operatorInitials = (operator = {}) => String(operator.displayName || operator.name || "?")
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0])
  .join("")
  .toUpperCase();

const statusByValue = new Map(SPA_STATUSES.map((status) => [status.value, status]));

const DAYS = [0, 1, 2, 3, 4, 5, 6];

const emptyDraft = (date) => ({
  id: null,
  date,
  startTime: "09:00",
  endTime: "",
  clientId: "",
  clientName: "",
  roomNumber: "",
  customerType: "HOTEL_GUEST",
  treatmentId: "",
  operatorId: "",
  resourceId: "",
  equipmentSelections: [],
  selectedCombination: null,
  addOns: [],
  originalAddOns: [],
  partySize: 1,
  participantNames: "",
  bookingGroupId: "",
  pmsReservationId: "",
  pmsBillId: "",
  pmsGuestId: "",
  pmsReference: {},
  pmsSync: "",
  status: "OPTION",
  statusReason: "",
  notes: "",
});

const minutesToTime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
};

const timeToMinutes = (value) => {
  const [hours, minutes] = String(value || "00:00").split(":").map(Number);
  return (Number(hours) || 0) * 60 + (Number(minutes) || 0);
};

const normalizeEndTimeForStart = (startTime, endTime) => {
  const value = String(endTime || "").trim();
  const match = value.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!match) return value;
  const startMinutes = timeToMinutes(startTime);
  const endMinutes = timeToMinutes(value);
  if (endMinutes > startMinutes) return value;

  const hour = Number(match[1]);
  const minutes = match[2];
  if (hour === 0 && startMinutes < 12 * 60) {
    return `12:${minutes}`;
  }
  return value;
};

const normalizeSchedulingSettings = (settings = {}) => ({
  ...DEFAULT_SPA_SCHEDULING_SETTINGS,
  ...settings,
  openingHours: Array.isArray(settings.openingHours) ? settings.openingHours : DEFAULT_SPA_SCHEDULING_SETTINGS.openingHours,
  closedDays: Array.isArray(settings.closedDays) ? settings.closedDays : DEFAULT_SPA_SCHEDULING_SETTINGS.closedDays,
  holidays: Array.isArray(settings.holidays) ? settings.holidays : DEFAULT_SPA_SCHEDULING_SETTINGS.holidays,
  plannerInterval: [15, 30].includes(Number(settings.plannerInterval)) ? Number(settings.plannerInterval) : DEFAULT_SPA_SCHEDULING_SETTINGS.plannerInterval,
});

const isWithinOpeningHour = (timeValue, openingHour) => {
  if (!openingHour?.start || !openingHour?.end) return false;
  const value = timeToMinutes(timeValue);
  const start = timeToMinutes(openingHour.start);
  const end = timeToMinutes(openingHour.end);
  if (start === end) return false;
  return start < end
    ? value >= start && value < end
    : value >= start || value < end;
};

const sortResources = (items = []) => [...items].sort((a, b) => {
  const areaDiff = String(a.area || "").localeCompare(String(b.area || ""), undefined, { numeric: true, sensitivity: "base" });
  if (areaDiff !== 0) return areaDiff;
  return String(a.name || "").localeCompare(String(b.name || ""), undefined, { numeric: true, sensitivity: "base" });
});

const mapTableToSpaResource = (table) => ({
  id: Number(table.id),
  tableId: Number(table.id),
  roomId: Number(table.roomId || table.room?.id || table.id),
  name: table.name || `Cabin ${table.id}`,
  area: table.room?.name || table.area || "SPA",
  capacity: Number(table.maxSeats || table.capacity || 1),
  status: table.status || "available",
  active: table.active !== false,
  source: "table-map",
});

const isAreaResource = (resource) => /^area\s*\d+$/i.test(String(resource?.name || "").trim());

const sameResourceName = (left, right) => (
  String(left || "").trim().toLowerCase() === String(right || "").trim().toLowerCase()
);

const sortOperators = (items = []) => [...items].sort((a, b) => (
  String(a.displayName || a.name || "").localeCompare(String(b.displayName || b.name || ""), undefined, { numeric: true, sensitivity: "base" })
));

const sortTreatments = (items = []) => [...items].sort((a, b) => (
  String(a.name || "").localeCompare(String(b.name || ""), undefined, { numeric: true, sensitivity: "base" })
));

function resolveEndTime(startTime, treatment) {
  return minutesToTime(timeToMinutes(startTime) + Number(treatment?.durationMinutes || 0));
}

const displayClientName = (client) => (
  [client?.name, client?.surname].filter(Boolean).join(" ").trim()
  || client?.email
  || `Client ${client?.id ?? ""}`.trim()
);

const parseManualClient = (value) => {
  const parts = String(value || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return null;
  return {
    name: parts.slice(0, -1).join(" "),
    surname: parts[parts.length - 1],
  };
};

const parseParticipants = (value) => String(value || "")
  .split("\n")
  .map((line) => line.trim())
  .filter(Boolean)
  .map((line) => {
    const [name, ...surnameParts] = line.split(/\s+/);
    return { name, surname: surnameParts.join(" ") || null };
  });

const commercialTransitions = {
  OPTION: ["CONFIRMED", "GUARANTEED", "CANCELLED"],
  CONFIRMED: ["GUARANTEED", "CANCELLED", "NO_SHOW"],
  GUARANTEED: ["CANCELLED", "NO_SHOW"],
  CANCELLED: [],
  NO_SHOW: [],
};

const finalCommercialStatuses = new Set(["CANCELLED", "NO_SHOW"]);
const cancellationFeeStatuses = new Set(["CANCELLED", "NO_SHOW"]);

const emptyCancellationFeeDraft = {
  open: false,
  appointmentId: null,
  status: "CANCELLED",
  reason: "",
  finalAmount: "",
  adjustmentReason: "",
  preview: null,
  loading: false,
  saving: false,
  error: "",
};

const emptyBillingDraft = {
  appointmentId: null,
  order: null,
  loading: false,
  error: "",
  checkoutOpen: false,
  checkoutSubmitting: false,
  storedValueInstruments: [],
  storedValue: [],
  payments: [{ method: "CARD", amount: "", isFiscal: true, note: "" }],
  discount: "",
  discountAmount: "",
  checkoutKey: "",
};

const emptyPmsDraft = (date = todayKey()) => ({
  search: "",
  from: date,
  to: addDays(date, 7),
  loading: false,
  linking: false,
  syncing: false,
  chargesLoading: false,
  reversing: false,
  results: [],
  charges: [],
  error: "",
});

const money = (value) => `EUR ${Number(value || 0).toFixed(2)}`;

const normalizeAddOnSelections = (items = []) => items
  .map((item) => ({
    addOnId: Number(item.addOnId ?? item.addonId ?? item.id),
    quantity: Math.min(100, Math.max(1, Number(item.quantity || 1))),
  }))
  .filter((item) => item.addOnId);

const addOnSignature = (items = []) => JSON.stringify(
  normalizeAddOnSelections(items).sort((left, right) => left.addOnId - right.addOnId)
);

const orderTotal = (order) => Number(
  order?.total
  ?? order?.totalAmount
  ?? order?.grandTotal
  ?? order?.amountDue
  ?? order?.summary?.total
  ?? 0
);

const orderIdOf = (order) => order?.id || order?.orderId || order?.data?.id || order?.order?.id || null;

const normalizeOrderPayload = (payload) => payload?.order || payload?.data?.order || payload?.data || payload;

const flattenPmsReservations = (reservations = []) => reservations.flatMap((reservation) => {
  const occupants = Array.isArray(reservation?.occupants) ? reservation.occupants : [];
  if (!occupants.length) {
    const room = reservation?.roomNumber || reservation?.roomnr || reservation?.room || reservation?.camera;
    const name = reservation?.fullName || reservation?.guestName || reservation?.customerName || reservation?.name || reservation?.holderName || reservation?.holder_name;
    return room && name ? [reservation] : [];
  }
  return occupants.map((occupant) => ({
    ...reservation,
    ...occupant,
    occupants: undefined,
    reservationId: reservation.reservationId,
    reservationStatus: reservation.status,
    arrival: reservation.arrival,
    departure: reservation.departure,
  })).filter((reservationItem) => {
    const room = reservationItem.roomNumber || reservationItem.roomnr || reservationItem.room || reservationItem.camera;
    const name = reservationItem.fullName || reservationItem.guestName || reservationItem.customerName || reservationItem.name || reservationItem.holderName || reservationItem.holder_name;
    return Boolean(room && name);
  });
});

const normalizeArrayPayload = (payload) => {
  if (Array.isArray(payload)) return flattenPmsReservations(payload);
  if (Array.isArray(payload?.data)) return flattenPmsReservations(payload.data);
  if (Array.isArray(payload?.reservations)) return flattenPmsReservations(payload.reservations);
  if (Array.isArray(payload?.items)) return flattenPmsReservations(payload.items);
  return [];
};

const reservationIdOf = (reservation) => (
  reservation?.reservationId
  || reservation?.id
  || reservation?.prenotazioneId
  || reservation?.reservation?.id
  || ""
);

const billIdOf = (reservation) => (
  reservation?.billId
  || reservation?.contoId
  || reservation?.bill?.id
  || reservation?.pmsBillId
  || ""
);

const guestIdOf = (reservation) => (
  reservation?.guestId
  || reservation?.occupantId
  || reservation?.customerId
  || reservation?.guest?.id
  || ""
);

const pmsGuestNameOf = (reservation = {}) => (
  reservation.fullName
  || reservation.guestName
  || reservation.customerName
  || reservation.name
  || reservation.holderName
  || reservation.holder_name
  || [reservation.guest?.name, reservation.guest?.surname].filter(Boolean).join(" ").trim()
  || ""
);

const pmsRoomNumberOf = (reservation = {}) => (
  reservation.roomNumber
  || reservation.roomnr
  || reservation.room
  || reservation.camera
  || reservation.bill?.roomNumber
  || ""
);

const formatPmsDate = (value) => {
  if (!value) return "-";
  const text = String(value);
  const compactMatch = text.match(/^(\d{4})(\d{2})(\d{2})$/);
  const date = compactMatch
    ? new Date(Number(compactMatch[1]), Number(compactMatch[2]) - 1, Number(compactMatch[3]))
    : new Date(value);
  if (Number.isNaN(date.getTime())) return text;
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}-${month}-${date.getFullYear()}`;
};

const pmsReservationNumberOf = (reservation = {}) => (
  reservation.number
  || reservation.bookingNumber
  || reservation.bookingNo
  || reservation.reservationNumber
  || reservationIdOf(reservation)
  || "-"
);

const pmsDateRangeLabel = (reservation = {}) => {
  const arrival = reservation.arrival || reservation.startDate || reservation.fromDate || reservation.checkIn;
  const departure = reservation.departure || reservation.endDate || reservation.toDate || reservation.checkOut;
  if (!arrival && !departure) return "";
  return `${formatPmsDate(arrival)} - ${formatPmsDate(departure)}`;
};

const pmsReservationLabel = (reservation = {}) => {
  const name = pmsGuestNameOf(reservation) || "PMS guest";
  const room = pmsRoomNumberOf(reservation) || "-";
  const reservationId = reservationIdOf(reservation);
  const billId = billIdOf(reservation);
  return `${name} | Room ${room}${reservationId ? ` | Res ${reservationId}` : ""}${billId ? ` | Bill ${billId}` : ""}`;
};

const appointmentCheckoutKey = (tenant, appointmentId) => {
  const storageKey = `spaCheckoutKey:${tenant || "tenant"}:${appointmentId}`;
  const existing = window.localStorage.getItem(storageKey);
  if (existing) return existing;
  const created = `spa-checkout-appointment-${appointmentId}-${Date.now()}`;
  window.localStorage.setItem(storageKey, created);
  return created;
};

const externalEngagementUrl = (token, tenant) => {
  if (!token) return "";
  const params = new URLSearchParams({ token });
  if (tenant) params.set("tenant", tenant);
  return `${window.location.origin}/spa/external-engagement#${params.toString()}`;
};

const formatLocalDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const feeAmount = (preview) => Number(
  preview?.calculatedFee
  ?? preview?.calculatedAmount
  ?? preview?.feeAmount
  ?? preview?.amount
  ?? 0
);

const formatCombinationLabel = (combination, operatorById, resourceById, equipmentById) => {
  const operatorNames = (combination.operatorIds || [])
    .map((id) => operatorById.get(Number(id))?.displayName || operatorById.get(Number(id))?.name || `Operator ${id}`)
    .join(", ");
  const cabinNames = (combination.cabinIds || [])
    .map((id) => resourceById.get(Number(id))?.name || `Cabin ${id}`)
    .join(", ");
  const equipmentNames = (combination.equipment || [])
    .map((item) => {
      const equipment = equipmentById.get(Number(item.equipmentId));
      return `${equipment?.name || `Equipment ${item.equipmentId}`} x${item.quantity}`;
    })
    .join(", ");
  return [operatorNames, cabinNames, equipmentNames].filter(Boolean).join(" | ") || "-";
};

const positiveIntegerIds = (values = []) => (
  Array.isArray(values)
    ? values.map(Number).filter((value) => Number.isInteger(value) && value > 0)
    : []
);

const cleanResourceSelection = (selection = {}, fallback = {}) => {
  const operatorIds = positiveIntegerIds(selection.operatorIds);
  const cabinIds = positiveIntegerIds(selection.cabinIds);
  const fallbackOperatorId = Number(fallback.operatorId || 0);
  const fallbackCabinId = Number(fallback.cabinId || 0);
  return {
    operatorIds: operatorIds.length
      ? operatorIds
      : Number.isInteger(fallbackOperatorId) && fallbackOperatorId > 0 ? [fallbackOperatorId] : [],
    cabinIds: cabinIds.length
      ? cabinIds
      : Number.isInteger(fallbackCabinId) && fallbackCabinId > 0 ? [fallbackCabinId] : [],
    equipment: (Array.isArray(selection.equipment) ? selection.equipment : [])
      .map((item) => ({
        equipmentId: Number(item.equipmentId ?? item.id),
        quantity: Number(item.quantity || 1),
      }))
      .filter((item) => Number.isInteger(item.equipmentId) && item.equipmentId > 0 && Number.isInteger(item.quantity) && item.quantity > 0),
  };
};

const selectedFirstIds = (selectedId, existingIds = []) => {
  const selected = Number(selectedId || 0);
  const existing = positiveIntegerIds(existingIds);
  if (!Number.isInteger(selected) || selected <= 0) return existing;
  return [selected, ...existing.filter((id) => id !== selected)];
};

function SpaPlannerPage() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { can } = usePermissions();
  const isDark = theme.palette.mode === "dark";
  const tenantType = String(localStorage.getItem("tenantType") || "").toUpperCase();
  const token = localStorage.getItem("accessToken");
  const tenant = localStorage.getItem("tenantId");
  const canManageAppointments = can("spa-appointments:view") && can("spa-appointments:manage");
  const canViewResources = can("spa-resources:view");
  const canManageResources = canViewResources && can("spa-resources:manage");
  const canViewFinance = can("spa-finance:view");
  const canManageFinance = canViewFinance && can("spa-finance:manage");
  const canConfirmCancellation = canManageAppointments && canManageFinance;
  const canOpenCashier = canManageFinance && can("receipts:manage");
  const canViewCatalog = can("spa-catalogs:view") || canViewFinance;
  const canViewClientRegistry = can("spa-clients:view") || can("clients:view");
  const canViewTables = can("tables:view");
  const canViewSettings = can("spa-settings:view");
  const canManageSettings = canViewSettings && can("spa-settings:manage");
  const [view, setView] = useState("day");
  const [selectedDate, setSelectedDate] = useState(() => {
    const date = new URLSearchParams(location.search).get("date");
    return /^\d{4}-\d{2}-\d{2}$/.test(date || "") && Number.isFinite(new Date(`${date}T00:00:00`).getTime())
      ? date : todayKey();
  });
  const [operatorId, setOperatorId] = useState("");
  const [resourceId, setResourceId] = useState("");
  const [status, setStatus] = useState("");
  const [treatments, setTreatments] = useState([]);
  const [packages, setPackages] = useState([]);
  const [operators, setOperators] = useState([]);
  const [resources, setResources] = useState([]);
  const [backendCabins, setBackendCabins] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [clients, setClients] = useState([]);
  const [addons, setAddons] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [plannerNotes, setPlannerNotes] = useState([]);
  const [noteDraft, setNoteDraft] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [groupDialog, setGroupDialog] = useState({ open: false, groupId: null });
  const [draft, setDraft] = useState(() => emptyDraft(selectedDate));
  const [saving, setSaving] = useState(false);
  const [appointmentDetailLoading, setAppointmentDetailLoading] = useState(false);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityState, setAvailabilityResult] = useState(null);
  const [smartSearching, setSmartSearching] = useState(false);
  const availabilityKey = JSON.stringify([token, tenant, dialogOpen, canManageAppointments, draft.id, draft.treatmentId, draft.addOns, draft.date, draft.startTime, draft.endTime, draft.partySize, draft.operatorId, draft.resourceId, draft.smartSlot?.startAt]);
  const availabilityKeyRef = useRef(availabilityKey);
  availabilityKeyRef.current = availabilityKey;
  const availabilityRequest = useRef(0);
  const availabilityResult = availabilityState?.key === availabilityKey ? availabilityState.data : null;
  const rejectionLabel = (rejection) => formatSpaAvailabilityRejection(rejection, t);
  const [dialogError, setDialogError] = useState("");
  const [schedulingSettings, setSchedulingSettings] = useState(() => normalizeSchedulingSettings());
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsError, setSettingsError] = useState("");
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [settingsDraft, setSettingsDraft] = useState(() => normalizeSchedulingSettings());
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [cancellationPolicyDraft, setCancellationPolicyDraft] = useState(defaultCancellationPolicyDraft);
  const [cancellationFeeDraft, setCancellationFeeDraft] = useState(emptyCancellationFeeDraft);
  const [billingDraft, setBillingDraft] = useState(emptyBillingDraft);
  const [pmsDraft, setPmsDraft] = useState(() => emptyPmsDraft(selectedDate));
  const [helitePickerOpen, setHelitePickerOpen] = useState(false);
  const [engagementInvitations, setEngagementInvitations] = useState([]);
  const [engagementDraft, setEngagementDraft] = useState({ expiresInHours: 168 });
  const [engagementBusy, setEngagementBusy] = useState(false);
  const [engagementToken, setEngagementToken] = useState("");
  const [draggedAppointmentId, setDraggedAppointmentId] = useState(null);
  const lastHandledSearchRef = useRef("");

  const loadPlannerData = useCallback(async () => {
    if (!token || !tenant) {
      setAppointments([]);
      setPlannerNotes([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const plannerData = await getSpaPlanner({
        date: selectedDate,
        view,
        operatorId,
        cabinId: resourceId,
        commercialStatus: status,
      }, token, tenant);
      setAppointments(plannerData.appointments || []);
      setPlannerNotes(plannerData.notes || []);
    } catch (error) {
      setAppointments([]);
      setPlannerNotes([]);
      showSnackbar(error?.message || t("spa.planner.loadError", { defaultValue: "Errore caricamento appuntamenti SPA" }), "error");
    } finally {
      setLoading(false);
    }
  }, [operatorId, resourceId, selectedDate, showSnackbar, status, t, tenant, token, view]);

  useEffect(() => {
    loadPlannerData();
  }, [loadPlannerData]);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams(location.search);
    const queryResourceId = params.get("resourceId");
    const queryResourceName = params.get("resourceName");
    Promise.all([
      token && tenant && canViewCatalog
        ? getSpaTreatments(token, tenant).catch(() => [])
        : Promise.resolve([]),
      token && tenant && canViewCatalog
        ? getAllPackages(token, tenant).catch(() => [])
        : Promise.resolve([]),
      token && tenant && (canViewResources || canViewFinance)
        ? getSpaCabins(token, tenant).catch(() => [])
        : Promise.resolve([]),
      token && tenant && canViewTables
        ? getAllTavoli(token, tenant, { noCache: true }).catch(() => [])
        : Promise.resolve([]),
      token && tenant && (canViewResources || canViewFinance)
        ? getSpaOperators(token, tenant).catch(() => [])
        : Promise.resolve([]),
      token && tenant && canViewResources
        ? getSpaEquipment(token, tenant).catch(() => [])
        : Promise.resolve([]),
      token && tenant && canViewClientRegistry
        ? getAllClienti(token, tenant).catch(() => [])
        : Promise.resolve([]),
    ])
      .then(([treatmentData, packageData, cabinData, tableData, operatorData, equipmentData, clientData]) => {
        if (cancelled) return;
        setTreatments(sortTreatments(treatmentData));
        setPackages(Array.isArray(packageData) ? packageData : []);
        setOperators(sortOperators(operatorData));
        setEquipment(equipmentData);
        setClients(Array.isArray(clientData) ? clientData : []);
        const mappedTables = (Array.isArray(tableData) ? tableData : [])
          .map(mapTableToSpaResource)
          .filter((resource) => resource.id && !isAreaResource(resource));
        const spaCabins = (Array.isArray(cabinData) ? cabinData : [])
          .filter((resource) => resource.id && !isAreaResource(resource));
        setBackendCabins(spaCabins);
        const nextResources = sortResources(spaCabins.length ? spaCabins : mappedTables);
        if (queryResourceId && !nextResources.some((resource) => String(resource.id) === String(queryResourceId))) {
          const legacyResource = mappedTables.find((resource) => String(resource.id) === String(queryResourceId));
          nextResources.push({
            ...legacyResource,
            id: Number(queryResourceId),
            name: queryResourceName || legacyResource?.name || `Cabin ${queryResourceId}`,
            area: "SPA",
            status: "available",
          });
        }
        setResources(nextResources);
      });
    return () => {
      cancelled = true;
    };
  }, [location.search, tenant, token, canViewCatalog, canViewResources, canViewFinance, canViewTables, canViewClientRegistry]);

  useEffect(() => {
    let cancelled = false;
    setSettingsLoading(true);
    setSettingsError("");
    if (!token || !tenant || !canViewSettings) {
      setSchedulingSettings(normalizeSchedulingSettings());
      setSettingsDraft(normalizeSchedulingSettings());
      setSettingsLoading(false);
      return;
    }
    getSpaSchedulingSettings(token, tenant)
      .then((data) => {
        if (cancelled) return;
        const normalized = normalizeSchedulingSettings(data);
        setSchedulingSettings(normalized);
        setSettingsDraft(normalized);
      })
      .catch((error) => {
        if (cancelled) return;
        setSettingsError(error?.message || t("spa.settings.loadError"));
        const fallback = normalizeSchedulingSettings();
        setSchedulingSettings(fallback);
        setSettingsDraft(fallback);
      })
      .finally(() => {
        if (!cancelled) setSettingsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenant, token, t, canViewSettings]);

  useEffect(() => {
    let cancelled = false;
    if (!token || !tenant || !canViewSettings) return () => {
      cancelled = true;
    };
    getSpaCancellationPolicy(token, tenant)
      .then((policy) => {
        if (cancelled) return;
        setCancellationPolicyDraft(cancellationPolicyToDraft(policy || {}));
      })
      .catch(() => {
        if (cancelled) return;
        setCancellationPolicyDraft(defaultCancellationPolicyDraft);
      });
    return () => {
      cancelled = true;
    };
  }, [tenant, token, canViewSettings]);

  useEffect(() => {
    if (loading || lastHandledSearchRef.current === location.search) return;
    const params = new URLSearchParams(location.search);
    const queryResourceId = params.get("resourceId");
    const queryAppointmentId = params.get("appointmentId");
    const queryPartySize = Number(params.get("partySize"));
    if (!queryResourceId && !queryAppointmentId) return;

    if (queryAppointmentId) {
      const appointment = appointments.find((item) => Number(item.id) === Number(queryAppointmentId));
      if (!appointment) return;
      const visibleCabinId = queryResourceId || String(appointment.resourceId || "");
      if (visibleCabinId) setResourceId(visibleCabinId);
      setDialogError("");
      setAvailabilityResult(null);
      setDraft({
        ...appointment,
        treatmentId: appointment.treatmentId ? String(appointment.treatmentId) : "",
        operatorId: appointment.operatorId ? String(appointment.operatorId) : "",
        resourceId: visibleCabinId,
        clientId: appointment.clientId ? String(appointment.clientId) : "",
        partySize: Number(appointment.partySize || 1),
        addOns: [],
        originalAddOns: [],
        statusReason: "",
      });
      setPmsDraft(emptyPmsDraft(appointment.date || selectedDate));
      setDialogOpen(true);
      setAppointmentDetailLoading(true);
      getSpaAppointment(appointment.id, token, tenant)
        .then((detail) => {
          const selectedAddOns = normalizeAddOnSelections(detail.addOns);
          setDraft((current) => ({
            ...current,
            ...detail,
            treatmentId: detail.treatmentId ? String(detail.treatmentId) : current.treatmentId,
            operatorId: detail.operatorId ? String(detail.operatorId) : current.operatorId,
            resourceId: queryResourceId || (detail.resourceId ? String(detail.resourceId) : current.resourceId),
            clientId: detail.clientId ? String(detail.clientId) : current.clientId,
            partySize: Number(detail.partySize || current.partySize || 1),
            addOns: selectedAddOns,
            originalAddOns: selectedAddOns,
            statusReason: "",
          }));
        })
        .catch((error) => setDialogError(error?.message || t("spa.planner.loadAppointmentError", { defaultValue: "Unable to load appointment details" })))
        .finally(() => setAppointmentDetailLoading(false));
      lastHandledSearchRef.current = location.search;
      return;
    }

    const shouldOpenNewAppointment = params.get("new") === "1";
    setResourceId(queryResourceId);
    if (shouldOpenNewAppointment) {
      setDraft({
        ...emptyDraft(selectedDate),
        resourceId: queryResourceId,
        partySize: Number.isInteger(queryPartySize) && queryPartySize > 0 ? queryPartySize : 1,
      });
      setPmsDraft(emptyPmsDraft(selectedDate));
      setDialogOpen(true);
    }
    lastHandledSearchRef.current = location.search;
  }, [appointments, loading, location.search, selectedDate, t, tenant, token]);

  useEffect(() => {
    let cancelled = false;
    setAddons([]);
    if (!canViewCatalog || !draft.treatmentId || !token || !tenant) return undefined;
    getSpaTreatmentAddons(draft.treatmentId, token, tenant)
      .then((data) => {
        if (!cancelled) setAddons(data);
      })
      .catch(() => {
        if (!cancelled) setAddons([]);
      });
    return () => {
      cancelled = true;
    };
  }, [draft.treatmentId, tenant, token, canViewCatalog]);

  const treatmentById = useMemo(
    () => new Map(treatments.map((item) => [Number(item.id), item])),
    [treatments]
  );
  const selectedAddOnDetails = useMemo(() => normalizeAddOnSelections(draft.addOns).map((selection) => {
    const addOn = addons.find((item) => Number(item.id) === selection.addOnId);
    return addOn ? { ...addOn, ...selection } : null;
  }).filter(Boolean), [addons, draft.addOns]);
  const addOnSummary = useMemo(() => {
    const treatment = treatmentById.get(Number(draft.treatmentId));
    const addOnTotal = selectedAddOnDetails.reduce((total, addOn) => (
      total + Number(addOn.priceAdjustment ?? addOn.priceDelta ?? 0) * addOn.quantity
    ), 0);
    const addOnDurationMinutes = selectedAddOnDetails.reduce((total, addOn) => (
      total + Number(addOn.durationAdjustmentMinutes ?? addOn.durationDeltaMinutes ?? 0) * addOn.quantity
    ), 0);
    const calculated = availabilityResult?.treatment;
    return {
      basePrice: Number(calculated?.basePrice ?? treatment?.price ?? draft.basePrice ?? 0),
      addOnTotal: Number(calculated?.addOnTotal ?? addOnTotal),
      finalPrice: Number(calculated?.price ?? calculated?.finalPrice ?? (Number(treatment?.price || draft.basePrice || 0) + addOnTotal)),
      baseDurationMinutes: Number(calculated?.baseDurationMinutes ?? treatment?.durationMinutes ?? draft.baseDurationMinutes ?? 0),
      addOnDurationMinutes: Number(calculated?.addOnDurationMinutes ?? addOnDurationMinutes),
      finalDurationMinutes: Number(calculated?.durationMinutes ?? calculated?.finalDurationMinutes ?? (Number(treatment?.durationMinutes || draft.baseDurationMinutes || 0) + addOnDurationMinutes)),
      resources: availabilityResult?.resourceRequirements || draft.requiredResources || {},
    };
  }, [availabilityResult, draft.baseDurationMinutes, draft.basePrice, draft.requiredResources, draft.treatmentId, selectedAddOnDetails, treatmentById]);

  const toggleAddOn = (addOn, checked) => {
    setDraft((current) => {
      const selections = normalizeAddOnSelections(current.addOns);
      const addOnId = Number(addOn.id);
      let next = checked
        ? [...selections.filter((item) => item.addOnId !== addOnId), { addOnId, quantity: 1 }]
        : selections.filter((item) => item.addOnId !== addOnId);
      if (checked && addOn.type === "TREATMENT_UPGRADE") {
        const upgradeIds = new Set(addons
          .filter((item) => item.type === "TREATMENT_UPGRADE" && Number(item.id) !== addOnId)
          .map((item) => Number(item.id)));
        next = next.filter((item) => !upgradeIds.has(item.addOnId));
      }
      return { ...current, addOns: next, selectedCombination: null, equipmentSelections: current.id ? current.equipmentSelections : [] };
    });
    setAvailabilityResult(null);
  };

  const updateAddOnQuantity = (addOnId, value) => {
    const quantity = Math.min(100, Math.max(1, Number(value || 1)));
    setDraft((current) => ({
      ...current,
      addOns: normalizeAddOnSelections(current.addOns).map((item) => (
        item.addOnId === Number(addOnId) ? { ...item, quantity } : item
      )),
      selectedCombination: null,
      equipmentSelections: current.id ? current.equipmentSelections : [],
    }));
    setAvailabilityResult(null);
  };
  const operatorById = useMemo(
    () => new Map(operators.map((item) => [Number(item.id), item])),
    [operators]
  );
  const resourceById = useMemo(
    () => new Map(resources.map((item) => [Number(item.id), item])),
    [resources]
  );
  const equipmentById = useMemo(
    () => new Map(equipment.map((item) => [Number(item.id), item])),
    [equipment]
  );
  const selectedDraftOperator = operatorById.get(Number(draft.operatorId));
  const engagementUrl = useMemo(
    () => externalEngagementUrl(engagementToken, tenant),
    [engagementToken, tenant]
  );

  const loadEngagementInvitations = useCallback(async (operatorIdValue = draft.operatorId, appointmentIdValue = draft.id) => {
    if (!canViewResources || !operatorIdValue || !appointmentIdValue || !token || !tenant) {
      setEngagementInvitations([]);
      return;
    }
    try {
      const invitations = await getSpaOperatorInvitations(operatorIdValue, token, tenant);
      setEngagementInvitations(
        (invitations || []).filter((invitation) => Number(invitation.appointmentId) === Number(appointmentIdValue))
      );
    } catch (_) {
      setEngagementInvitations([]);
    }
  }, [draft.id, draft.operatorId, tenant, token, canViewResources]);

  useEffect(() => {
    if (!dialogOpen || !draft.id || !draft.operatorId) {
      setEngagementInvitations([]);
      setEngagementToken("");
      return;
    }
    loadEngagementInvitations(draft.operatorId, draft.id);
  }, [dialogOpen, draft.id, draft.operatorId, loadEngagementInvitations]);

  const cabinNameFromAppointment = useCallback((appointment) => {
    const assignedCabin = Array.isArray(appointment?.assignedResources?.cabins)
      ? appointment.assignedResources.cabins.find((item) => item?.name && !isAreaResource(item))
      : null;
    if (assignedCabin?.name) return assignedCabin.name;

    const cabinIds = Array.isArray(appointment?.resourceCombination?.cabinIds)
      ? appointment.resourceCombination.cabinIds.map(Number).filter(Boolean)
      : [];
    const backendCabin = backendCabins.find((cabin) => cabinIds.includes(Number(cabin.id)));
    return backendCabin?.name || "";
  }, [backendCabins]);

  const resolveVisibleCabinId = useCallback((appointment) => {
    const cabinName = cabinNameFromAppointment(appointment);
    const matchingResource = resources.find((resource) => sameResourceName(resource.name, cabinName));
    if (matchingResource?.id) return String(matchingResource.id);

    const directResourceId = Number(appointment?.resourceId);
    if (resourceById.has(directResourceId)) return String(directResourceId);

    const cabinIds = Array.isArray(appointment?.resourceCombination?.cabinIds)
      ? appointment.resourceCombination.cabinIds.map(Number).filter(Boolean)
      : [];
    const directCombinationId = cabinIds.find((id) => resourceById.has(id));
    if (directCombinationId) return String(directCombinationId);

    return directResourceId ? String(directResourceId) : "";
  }, [cabinNameFromAppointment, resourceById, resources]);

  const appointmentMatchesResource = useCallback((appointment, selectedResourceId) => {
    if (!selectedResourceId) return true;
    if (Number(appointment?.resourceId) === Number(selectedResourceId)) return true;
    return Number(resolveVisibleCabinId(appointment)) === Number(selectedResourceId);
  }, [resolveVisibleCabinId]);

  const ensureBackendCabinId = async (resourceIdValue) => {
    const resource = resourceById.get(Number(resourceIdValue));
    if (!resource) return Number(resourceIdValue);
    if (resource.source !== "table-map") return Number(resource.id);

    const existing = backendCabins.find((cabin) => sameResourceName(cabin.name, resource.name));
    if (existing?.id) return Number(existing.id);
    if (!canManageResources) throw new Error(t("spa.operations.cabinSetupRequired"));

    const created = await createSpaCabin({
      name: resource.name,
      cabinType: "MASSAGE",
      capacity: Math.max(1, Number(resource.capacity || 1)),
      characteristics: [],
    }, token, tenant);
    setBackendCabins((current) => [...current, created]);
    return Number(created.id);
  };

  const visibleDates = useMemo(() => (
    view === "week"
      ? Array.from({ length: 7 }, (_, index) => addDays(selectedDate, index))
      : [selectedDate]
  ), [selectedDate, view]);

  const filteredAppointments = useMemo(() => (
    appointments
      .filter((appointment) => visibleDates.includes(appointment.date))
      .filter((appointment) => !operatorId || Number(appointment.operatorId) === Number(operatorId))
      .filter((appointment) => appointmentMatchesResource(appointment, resourceId))
      .filter((appointment) => !status || appointment.commercialStatus === status)
      .sort((a, b) => `${a.date} ${a.startTime}`.localeCompare(`${b.date} ${b.startTime}`))
  ), [appointmentMatchesResource, appointments, operatorId, resourceId, status, visibleDates]);

  const dayTimeline = useMemo(() => {
    const openingRanges = schedulingSettings.openingHours.filter((item) => {
      const date = new Date(`${selectedDate}T00:00:00`);
      return !Number.isNaN(date.getTime()) && Number(item.dayOfWeek) === date.getDay();
    });
    const appointmentMinutes = filteredAppointments
      .filter((appointment) => appointment.date === selectedDate)
      .flatMap((appointment) => [timeToMinutes(appointment.startTime), timeToMinutes(appointment.endTime)]);
    const openingMinutes = openingRanges.flatMap((item) => [timeToMinutes(item.start), timeToMinutes(item.end)]);
    const values = [...appointmentMinutes, ...openingMinutes].filter(Number.isFinite);
    const start = Math.floor((values.length ? Math.min(...values) : 9 * 60) / 60) * 60;
    const end = Math.ceil((values.length ? Math.max(...values) : 18 * 60) / 60) * 60;
    const safeStart = Math.min(start, 9 * 60);
    const safeEnd = Math.max(end, safeStart + 8 * 60);
    return {
      start: safeStart,
      end: safeEnd,
      hours: Array.from({ length: Math.floor((safeEnd - safeStart) / 60) + 1 }, (_, index) => safeStart + index * 60),
    };
  }, [filteredAppointments, schedulingSettings.openingHours, selectedDate]);

  const visibleOperators = useMemo(() => {
    const activeOperators = operators.filter((operator) => operator.active !== false);
    if (operatorId) return activeOperators.filter((operator) => Number(operator.id) === Number(operatorId));
    const assignedIds = new Set(filteredAppointments
      .filter((appointment) => appointment.date === selectedDate)
      .map((appointment) => Number(appointment.operatorId))
      .filter(Boolean));
    return activeOperators.length
      ? activeOperators
      : Array.from(assignedIds).map((id) => operatorById.get(id)).filter(Boolean);
  }, [filteredAppointments, operatorById, operatorId, operators, selectedDate]);

  const visiblePlannerNotes = useMemo(() => (
    plannerNotes.filter((note) => visibleDates.includes(note.date))
  ), [plannerNotes, visibleDates]);

  const openCreateDialog = () => {
    setDialogError("");
    setAvailabilityResult(null);
    setEngagementInvitations([]);
    setEngagementToken("");
    setEngagementDraft({ expiresInHours: 168 });
    setDraft(emptyDraft(selectedDate));
    setPmsDraft(emptyPmsDraft(selectedDate));
    setDialogOpen(true);
  };

  const openCreateGroupDialog = () => {
    setGroupDialog({ open: true, groupId: null });
  };

  const openManageGroupDialog = (groupId) => {
    const numericGroupId = Number(groupId);
    if (!Number.isInteger(numericGroupId) || numericGroupId <= 0) return;
    setDialogOpen(false);
    setGroupDialog({ open: true, groupId: numericGroupId });
  };

  const selectedDateDay = useMemo(() => {
    const date = new Date(`${selectedDate}T00:00:00`);
    return Number.isNaN(date.getTime()) ? null : date.getDay();
  }, [selectedDate]);

  const selectedDateOpeningHours = useMemo(() => (
    schedulingSettings.openingHours.filter((item) => Number(item.dayOfWeek) === Number(selectedDateDay))
  ), [schedulingSettings.openingHours, selectedDateDay]);

  const selectedHoliday = useMemo(() => (
    schedulingSettings.holidays.find((holiday) => holiday.date === selectedDate)
  ), [schedulingSettings.holidays, selectedDate]);

  const selectedDateClosed = selectedDateDay !== null && schedulingSettings.closedDays.includes(selectedDateDay);

  const getScheduleIssue = (dateKey, startTime) => {
    const date = new Date(`${dateKey}T00:00:00`);
    if (Number.isNaN(date.getTime())) return t("spa.settings.invalidDate");
    const day = date.getDay();
    if (schedulingSettings.closedDays.includes(day)) return t("spa.settings.closedDayWarning");
    const holiday = schedulingSettings.holidays.find((item) => item.date === dateKey);
    if (holiday) return t("spa.settings.holidayWarning", { name: holiday.name || dateKey });
    const minute = timeToMinutes(startTime) % 60;
    if (minute % schedulingSettings.plannerInterval !== 0) {
      return t("spa.settings.intervalWarning", { interval: schedulingSettings.plannerInterval });
    }
    const dayOpeningHours = schedulingSettings.openingHours.filter((item) => Number(item.dayOfWeek) === day);
    if (dayOpeningHours.length && !dayOpeningHours.some((item) => isWithinOpeningHour(startTime, item))) {
      return t("spa.settings.outsideOpeningHoursWarning");
    }
    return "";
  };

  const openEditDialog = async (appointment) => {
    setDialogError("");
    setAvailabilityResult(null);
    setEngagementToken("");
    setEngagementDraft({ expiresInHours: 168 });
    const visibleCabinId = resolveVisibleCabinId(appointment);
    const initialAddOns = normalizeAddOnSelections(appointment.addOns);
    setDraft({
      ...appointment,
      treatmentId: String(appointment.treatmentId),
      operatorId: String(appointment.operatorId),
      resourceId: visibleCabinId,
      clientId: appointment.clientId ? String(appointment.clientId) : "",
      partySize: Number(appointment.partySize || 1),
      addOns: initialAddOns,
      originalAddOns: initialAddOns,
      statusReason: "",
    });
    setPmsDraft(emptyPmsDraft(appointment.date || selectedDate));
    setDialogOpen(true);
    setAppointmentDetailLoading(true);
    try {
      const detail = await getSpaAppointment(appointment.id, token, tenant);
      const detailAddOns = normalizeAddOnSelections(detail.addOns);
      const detailCabinId = resolveVisibleCabinId(detail) || visibleCabinId;
      setDraft((current) => ({
        ...current,
        ...detail,
        treatmentId: detail.treatmentId ? String(detail.treatmentId) : current.treatmentId,
        operatorId: detail.operatorId ? String(detail.operatorId) : current.operatorId,
        resourceId: detailCabinId,
        clientId: detail.clientId ? String(detail.clientId) : current.clientId,
        partySize: Number(detail.partySize || current.partySize || 1),
        addOns: detailAddOns,
        originalAddOns: detailAddOns,
        statusReason: "",
      }));
    } catch (error) {
      setDialogError(error?.message || t("spa.planner.loadAppointmentError", { defaultValue: "Unable to load appointment details" }));
    } finally {
      setAppointmentDetailLoading(false);
    }
  };

  const updateAppointmentInState = (appointment) => {
    setAppointments((current) => (
      current.some((item) => Number(item.id) === Number(appointment.id))
        ? current.map((item) => (Number(item.id) === Number(appointment.id) ? appointment : item))
        : [...current, appointment]
    ));
  };

  const mergeAppointmentIntoDraft = (appointment) => {
    setDraft((current) => ({
      ...current,
      ...appointment,
      treatmentId: appointment.treatmentId ? String(appointment.treatmentId) : current.treatmentId,
      operatorId: appointment.operatorId ? String(appointment.operatorId) : current.operatorId,
      resourceId: appointment.resourceId ? String(appointment.resourceId) : current.resourceId,
      clientId: appointment.clientId ? String(appointment.clientId) : current.clientId,
      partySize: Number(appointment.partySize || current.partySize || 1),
      statusReason: current.statusReason || "",
    }));
  };

  const appointmentStartAt = () => draft.smartSlot?.localStart === `${draft.date}T${draft.startTime}` && draft.smartSlot.timeZone === schedulingSettings.timeZone
    ? draft.smartSlot.startAt : dateTimeWithLocalOffset(draft.date, draft.startTime);
  const buildAvailabilityPayload = (backendCabinId = Number(draft.resourceId)) => ({
    treatmentProductId: Number(draft.treatmentId),
    addOns: normalizeAddOnSelections(draft.addOns),
    startAt: appointmentStartAt(),
    partySize: Number(draft.partySize || 1),
    preferredResources: {
      operatorIds: draft.operatorId ? [Number(draft.operatorId)] : [],
      cabinIds: backendCabinId ? [Number(backendCabinId)] : [],
      equipmentIds: (draft.equipmentSelections || []).map((item) => Number(item.equipmentId)).filter(Boolean),
    },
  });

  const selectedResourcesPayload = (backendCabinId = Number(draft.resourceId)) => {
    const fallback = { operatorId: draft.operatorId, cabinId: backendCabinId };
    if (draft.selectedCombination) return cleanResourceSelection(draft.selectedCombination, fallback);
    if (draft.id && draft.resourceCombination) {
      const originalOperatorIds = Array.isArray(draft.resourceCombination.operatorIds)
        ? draft.resourceCombination.operatorIds.map(Number).filter(Boolean)
        : [];
      const originalCabinIds = Array.isArray(draft.resourceCombination.cabinIds)
        ? draft.resourceCombination.cabinIds.map(Number).filter(Boolean)
        : [];
      const operatorIdValue = Number(draft.operatorId);
      const cabinIdValue = Number(backendCabinId);
      return cleanResourceSelection({
        operatorIds: selectedFirstIds(operatorIdValue, originalOperatorIds),
        cabinIds: selectedFirstIds(cabinIdValue, originalCabinIds),
        equipment: (draft.equipmentSelections || draft.resourceCombination.equipment || [])
          .map((item) => ({ equipmentId: Number(item.equipmentId), quantity: Number(item.quantity || 1) }))
          .filter((item) => item.equipmentId && item.quantity > 0),
      }, fallback);
    }
    return cleanResourceSelection({
      operatorIds: draft.operatorId ? [Number(draft.operatorId)] : [],
      cabinIds: backendCabinId ? [Number(backendCabinId)] : [],
      equipment: (draft.equipmentSelections || [])
        .map((item) => ({ equipmentId: Number(item.equipmentId), quantity: Number(item.quantity || 1) }))
        .filter((item) => item.equipmentId && item.quantity > 0),
    }, fallback);
  };

  const appointmentAvailabilityPayload = (appointment, targetDate, backendCabinId) => ({
    treatmentProductId: Number(appointment.treatmentId),
    addOns: normalizeAddOnSelections(appointment.addOns),
    startAt: dateTimeWithLocalOffset(targetDate, appointment.startTime),
    partySize: Number(appointment.partySize || 1),
    preferredResources: {
      operatorIds: appointment.operatorId ? [Number(appointment.operatorId)] : [],
      cabinIds: backendCabinId ? [Number(backendCabinId)] : [],
      equipmentIds: (appointment.equipmentSelections || [])
        .map((item) => Number(item.equipmentId))
        .filter((value) => Number.isInteger(value) && value > 0),
    },
  });

  const buildCreatePayload = (backendCabinId = Number(draft.resourceId)) => {
    const clientId = Number(draft.clientId);
    const manualClient = clientId ? null : parseManualClient(draft.clientName);
    const participants = parseParticipants(draft.participantNames);
    const payload = {
      treatmentProductId: Number(draft.treatmentId),
      addOns: normalizeAddOnSelections(draft.addOns),
      startAt: appointmentStartAt(),
      partySize: Number(draft.partySize || Math.max(1, participants.length || 1)),
      customerType: draft.customerType,
      commercialStatus: draft.status,
      ...(draft.bookingGroupId ? { bookingGroupId: Number(draft.bookingGroupId) } : {}),
      participants,
      preferredResources: buildAvailabilityPayload(backendCabinId).preferredResources,
      resources: selectedResourcesPayload(backendCabinId),
      pmsReference: {
        reservationId: draft.pmsReservationId || null,
        billId: draft.pmsBillId || null,
        guestId: draft.pmsGuestId || null,
        roomNumber: draft.roomNumber || null,
      },
    };
    if (clientId) {
      payload.clientId = clientId;
    } else if (manualClient) {
      payload.client = manualClient;
    }
    return payload;
  };

  const showApiError = (error) => {
    if (error?.status === 409) {
      setAvailabilityResult(null);
      setDraft((current) => ({ ...current, selectedCombination: null, equipmentSelections: current.id ? current.equipmentSelections : [] }));
    }
    const rejections = Array.isArray(error?.rejections) ? error.rejections : [];
    const message = rejections.length
      ? rejections.map(rejectionLabel).join("\n")
      : error?.message || t("spa.planner.saveError", { defaultValue: "Errore salvataggio appuntamento" });
    setDialogError(message);
    showSnackbar(message, error?.status === 409 ? "warning" : "error");
  };

  const checkAvailability = async () => {
    const requestId = ++availabilityRequest.current;
    const requestKey = availabilityKey;
    setAvailabilityLoading(true);
    setAvailabilityResult(null);
    setDialogError("");
    try {
      const backendCabinId = await ensureBackendCabinId(draft.resourceId);
      const result = await checkSpaAppointmentAvailability(buildAvailabilityPayload(backendCabinId), token, tenant);
      if (requestId !== availabilityRequest.current || requestKey !== availabilityKeyRef.current) return;
      setAvailabilityResult({ key: requestKey, data: result });
      showSnackbar(
        result?.available
          ? t("spa.planner.availabilityAvailable", { defaultValue: "Disponibilita trovata" })
          : t("spa.planner.availabilityUnavailable", { defaultValue: "Nessuna combinazione disponibile" }),
        result?.available ? "success" : "warning"
      );
    } catch (error) {
      if (requestId === availabilityRequest.current && requestKey === availabilityKeyRef.current) showApiError(error);
    } finally {
      if (requestId === availabilityRequest.current) setAvailabilityLoading(false);
    }
  };

  const saveDraft = async () => {
    if (!canManageAppointments || saving || smartSearching || appointmentDetailLoading) return;
    const bookingKey = availabilityKey;
    const treatment = treatmentById.get(Number(draft.treatmentId));
    setSaving(true);
    setDialogError("");
    try {
      const backendCabinId = await ensureBackendCabinId(draft.resourceId);
      let saved;
      if (draft.id) {
        const fallbackEndTime = resolveEndTime(
          draft.startTime,
          Number(treatment?.durationMinutes) > 0 ? treatment : { durationMinutes: schedulingSettings.plannerInterval }
        );
        const endTime = normalizeEndTimeForStart(draft.startTime, draft.endTime || fallbackEndTime);
        saved = await rescheduleSpaAppointment(draft.id, {
          startAt: dateTimeWithLocalOffset(draft.date, draft.startTime),
          endAt: dateTimeWithLocalOffset(draft.date, endTime),
          preferredResources: buildAvailabilityPayload(backendCabinId).preferredResources,
          resources: selectedResourcesPayload(backendCabinId),
        }, token, tenant);
        if (addOnSignature(draft.addOns) !== addOnSignature(draft.originalAddOns)) {
          saved = await updateSpaAppointmentAddOns(draft.id, {
            addOns: normalizeAddOnSelections(draft.addOns),
            resources: selectedResourcesPayload(backendCabinId),
          }, token, tenant);
        }
      } else {
        let availability;
        try {
          availability = await checkSpaAppointmentAvailability(buildAvailabilityPayload(backendCabinId), token, tenant);
        } catch (error) {
          if (error.status || error.rejections?.length) throw error;
          throw new Error(t('spa.equipment.availabilityError'));
        }
        if (bookingKey !== availabilityKeyRef.current) throw new Error(t('spa.equipment.availabilityChanged'));
        if (!availability?.available || !availability.combinations?.length) {
          const error = new Error(t('spa.equipment.availabilityUnavailable'));
          error.rejections = availability?.rejections || [];
          throw error;
        }
        const combination = draft.selectedCombination
          ? availability.combinations.find((item) => sameSpaResourceCombination(item, draft.selectedCombination))
          : availability.combinations.find((item) => (!draft.operatorId || item.operatorIds?.includes(Number(draft.operatorId)))
            && (!backendCabinId || item.cabinIds?.includes(Number(backendCabinId))));
        if (!combination) throw new Error(t(draft.selectedCombination ? 'spa.equipment.selectionUnavailable' : 'spa.equipment.availabilityUnavailable'));
        const payload = buildCreatePayload(backendCabinId);
        payload.resources = cleanResourceSelection(combination);
        saved = await createSpaAppointment(payload, token, tenant);
        if (saved?.id && draft.pmsReservationId && draft.pmsBillId && draft.pmsGuestId) {
          saved = await linkSpaAppointmentPmsReservation(saved.id, {
            reservationId: draft.pmsReservationId,
            billId: draft.pmsBillId,
            guestId: draft.pmsGuestId,
          }, token, tenant);
        }
      }
      updateAppointmentInState(saved);
      await loadPlannerData();
      setDialogOpen(false);
      showSnackbar(t("spa.planner.saved", { defaultValue: "Appuntamento salvato" }), "success");
    } catch (error) {
      showApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleAppointmentDragStart = (event, appointment) => {
    if (!canManageAppointments) return;
    setDraggedAppointmentId(appointment.id);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(appointment.id));
  };

  const handleAppointmentDrop = async (event, targetDate) => {
    event.preventDefault();
    const appointmentId = Number(event.dataTransfer.getData("text/plain") || draggedAppointmentId);
    setDraggedAppointmentId(null);
    if (!appointmentId || !canManageAppointments) return;
    const appointment = appointments.find((item) => Number(item.id) === appointmentId);
    if (!appointment || appointment.date === targetDate) return;

    const treatment = treatmentById.get(Number(appointment.treatmentId));
    const fallbackEndTime = resolveEndTime(
      appointment.startTime,
      Number(treatment?.durationMinutes) > 0 ? treatment : { durationMinutes: schedulingSettings.plannerInterval }
    );
    const endTime = normalizeEndTimeForStart(appointment.startTime, appointment.endTime || fallbackEndTime);

    setSaving(true);
    try {
      const visibleCabinId = resolveVisibleCabinId(appointment);
      const backendCabinId = await ensureBackendCabinId(visibleCabinId || appointment.resourceId);
      const appointmentDetail = await getSpaAppointment(appointment.id, token, tenant);
      const availability = await checkSpaAppointmentAvailability(
        appointmentAvailabilityPayload(appointmentDetail, targetDate, backendCabinId),
        token,
        tenant
      );
      if (!availability?.available || !availability.combinations?.length) {
        const availabilityError = new Error(t("spa.planner.availabilityUnavailable", { defaultValue: "Nessuna combinazione disponibile" }));
        availabilityError.rejections = availability?.rejections || [];
        throw availabilityError;
      }
      const resourcesPayload = {
        preferredResources: availability.preferredResources,
        resources: cleanResourceSelection(availability.combinations[0], {
          operatorId: appointment.operatorId,
          cabinId: backendCabinId,
        }),
      };
      const updated = await rescheduleSpaAppointment(appointment.id, {
        startAt: dateTimeWithLocalOffset(targetDate, appointment.startTime),
        endAt: dateTimeWithLocalOffset(targetDate, endTime),
        ...resourcesPayload,
      }, token, tenant);
      updateAppointmentInState(updated);
      await loadPlannerData();
      showSnackbar(t("spa.planner.rescheduled", { defaultValue: "Appointment rescheduled" }), "success");
    } catch (error) {
      showApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleTimelineDrop = async (event, targetOperatorId) => {
    event.preventDefault();
    const appointmentId = Number(event.dataTransfer.getData("text/plain") || draggedAppointmentId);
    setDraggedAppointmentId(null);
    if (!appointmentId || !canManageAppointments) return;

    const appointment = appointments.find((item) => Number(item.id) === appointmentId);
    if (!appointment) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    const duration = Math.max(
      Number(schedulingSettings.plannerInterval || 15),
      timeToMinutes(appointment.endTime) - timeToMinutes(appointment.startTime)
    );
    const interval = Math.max(5, Number(schedulingSettings.plannerInterval || 15));
    const rawStart = dayTimeline.start + ratio * (dayTimeline.end - dayTimeline.start);
    const snappedStart = Math.round(rawStart / interval) * interval;
    const targetStartMinutes = Math.min(
      Math.max(dayTimeline.start, snappedStart),
      Math.max(dayTimeline.start, dayTimeline.end - duration)
    );
    const targetStartTime = minutesToTime(targetStartMinutes);
    const targetEndTime = minutesToTime(targetStartMinutes + duration);

    if (
      Number(appointment.operatorId) === Number(targetOperatorId)
      && appointment.startTime === targetStartTime
    ) return;

    setSaving(true);
    try {
      const visibleCabinId = resolveVisibleCabinId(appointment);
      const backendCabinId = await ensureBackendCabinId(visibleCabinId || appointment.resourceId);
      const resources = cleanResourceSelection({
        operatorIds: selectedFirstIds(targetOperatorId, appointment.resourceCombination?.operatorIds),
        cabinIds: selectedFirstIds(backendCabinId, appointment.resourceCombination?.cabinIds),
        equipment: appointment.resourceCombination?.equipment || appointment.equipmentSelections || [],
      }, {
        operatorId: targetOperatorId,
        cabinId: backendCabinId,
      });
      const updated = await rescheduleSpaAppointment(appointment.id, {
        startAt: dateTimeWithLocalOffset(selectedDate, targetStartTime),
        endAt: dateTimeWithLocalOffset(selectedDate, targetEndTime),
        preferredResources: {
          operatorIds: [Number(targetOperatorId)],
          cabinIds: backendCabinId ? [Number(backendCabinId)] : [],
          equipmentIds: (appointment.equipmentSelections || [])
            .map((item) => Number(item.equipmentId))
            .filter(Boolean),
        },
        resources,
      }, token, tenant);
      updateAppointmentInState(updated);
      await loadPlannerData();
      showSnackbar(t("spa.planner.rescheduled", { defaultValue: "Appointment rescheduled" }), "success");
    } catch (error) {
      showApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const changeCommercialStatus = async () => {
    if (!canManageAppointments || !draft.id || draft.status === draft.commercialStatus) return;
    if (cancellationFeeStatuses.has(draft.status)) {
      if (!canConfirmCancellation) return;
      setCancellationFeeDraft({
        ...emptyCancellationFeeDraft,
        open: true,
        appointmentId: draft.id,
        status: draft.status,
        reason: draft.statusReason,
        loading: true,
      });
      setSaving(true);
      setDialogError("");
      try {
        const preview = await getSpaCancellationFee(draft.id, draft.status, token, tenant);
        const amount = feeAmount(preview);
        setCancellationFeeDraft((current) => ({
          ...current,
          preview,
          finalAmount: amount ? String(amount.toFixed(2)) : "0",
          loading: false,
        }));
      } catch (error) {
        const message = error?.message || t("spa.cancellation.previewError", { defaultValue: "Error calculating cancellation fee" });
        setCancellationFeeDraft((current) => ({ ...current, error: message, loading: false }));
        showSnackbar(message, "error");
      } finally {
        setSaving(false);
      }
      return;
    }

    setSaving(true);
    setDialogError("");
    try {
      const updated = await changeSpaAppointmentCommercialStatus(draft.id, {
        status: draft.status,
        ...(draft.statusReason ? { reason: draft.statusReason } : {}),
      }, token, tenant);
      updateAppointmentInState(updated);
      setDraft((current) => ({ ...current, ...updated, treatmentId: String(updated.treatmentId), operatorId: String(updated.operatorId), resourceId: String(updated.resourceId) }));
      await loadPlannerData();
      showSnackbar(t("spa.planner.statusSaved", { defaultValue: "Stato aggiornato" }), "success");
    } catch (error) {
      showApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const confirmCancellationFee = async () => {
    const reason = cancellationFeeDraft.reason.trim();
    if (!canConfirmCancellation || !cancellationFeeDraft.preview || cancellationFeeDraft.loading || cancellationFeeDraft.saving || !cancellationFeeDraft.appointmentId || !reason) return;
    const calculated = feeAmount(cancellationFeeDraft.preview);
    const finalAmount = Number(cancellationFeeDraft.finalAmount || 0);
    const adjusted = Math.abs(finalAmount - calculated) > 0.009;
    if (adjusted && !cancellationFeeDraft.adjustmentReason.trim()) {
      setCancellationFeeDraft((current) => ({
        ...current,
        error: t("spa.cancellation.adjustmentReasonRequired", { defaultValue: "Adjustment reason is required when the fee changes." }),
      }));
      return;
    }

    setCancellationFeeDraft((current) => ({ ...current, saving: true, error: "" }));
    try {
      const updated = await postSpaCancellationFee(cancellationFeeDraft.appointmentId, {
        status: cancellationFeeDraft.status,
        reason,
        ...(adjusted ? {
          finalAmount,
          adjustmentReason: cancellationFeeDraft.adjustmentReason.trim(),
        } : {}),
      }, token, tenant);
      updateAppointmentInState(updated);
      await loadPlannerData();
      setCancellationFeeDraft(emptyCancellationFeeDraft);
      setDialogOpen(false);
      showSnackbar(t("spa.cancellation.saved", { defaultValue: "Cancellation/no-show saved" }), "success");
    } catch (error) {
      setCancellationFeeDraft((current) => ({
        ...current,
        error: error?.message || t("spa.cancellation.saveError", { defaultValue: "Error saving cancellation fee" }),
      }));
      showSnackbar(error?.message || t("spa.cancellation.saveError", { defaultValue: "Error saving cancellation fee" }), "error");
    } finally {
      setCancellationFeeDraft((current) => ({ ...current, saving: false }));
    }
  };

  const hasClientForCreate = Boolean(draft.clientId || parseManualClient(draft.clientName));
  const draftValid = Boolean(
    hasClientForCreate
    && draft.treatmentId
    && draft.operatorId
    && draft.resourceId
    && draft.date
    && draft.startTime
    && Number(draft.partySize) > 0
  );
  const draftScheduleIssue = getScheduleIssue(draft.date, draft.startTime);
  const draftValidationMessages = [
    !draft.clientId && !String(draft.clientName || "").trim()
      ? t("spa.validation.clientRequired", { defaultValue: "Select or enter a client." })
      : "",
    !draft.clientId && String(draft.clientName || "").trim() && !parseManualClient(draft.clientName)
      ? t("spa.validation.clientSurnameRequired", { defaultValue: "Enter client name and surname." })
      : "",
    !draft.treatmentId ? t("spa.validation.treatmentRequired", { defaultValue: "Select a treatment." }) : "",
    !draft.date || !draft.startTime ? t("spa.validation.dateTimeRequired", { defaultValue: "Select date and start time." }) : "",
    !draft.operatorId ? t("spa.validation.operatorRequired", { defaultValue: "Select an available operator." }) : "",
    !draft.resourceId ? t("spa.validation.cabinRequired", { defaultValue: "Select a cabin." }) : "",
    Number(draft.partySize) <= 0 ? t("spa.validation.partySizeRequired", { defaultValue: "Enter at least one participant." }) : "",
    draftScheduleIssue,
  ].filter(Boolean);
  const currentCommercialStatus = draft.commercialStatus || draft.status || "OPTION";
  const allowedNextStatuses = commercialTransitions[currentCommercialStatus] || [];
  const statusChangeRequiresReason = ["CANCELLED", "NO_SHOW"].includes(draft.status);
  const statusChangeValid = Boolean(
    draft.id
    && draft.status !== currentCommercialStatus
    && allowedNextStatuses.includes(draft.status)
    && (!statusChangeRequiresReason || draft.statusReason.trim())
  );
  const checkoutOrderTotal = orderTotal(billingDraft.order);
  const checkoutSubmittedTotal = (
    billingDraft.payments.reduce((sum, item) => sum + Number(item.amount || 0), 0)
    + billingDraft.storedValue.reduce((sum, item) => sum + Number(item.amount || 0), 0)
  );
  const checkoutPaymentsValid = billingDraft.payments.some((item) => Number(item.amount || 0) > 0)
    && billingDraft.payments.every((item) => item.method === "ROOM_CHARGE" || item.isFiscal);
  const pmsLinked = Boolean(draft.pmsReservationId || draft.pmsBillId || draft.pmsGuestId);

  const loadAppointmentOrder = async (appointment = draft) => {
    if (!canViewFinance || !appointment?.id) return null;
    setBillingDraft((current) => ({ ...current, appointmentId: appointment.id, loading: true, error: "" }));
    try {
      const response = await getSpaAppointmentOrder(appointment.id, token, tenant);
      const order = normalizeOrderPayload(response);
      setBillingDraft((current) => ({
        ...current,
        appointmentId: appointment.id,
        order,
        loading: false,
        checkoutKey: appointmentCheckoutKey(tenant, appointment.id),
      }));
      return order;
    } catch (error) {
      const message = error?.status === 404
        ? t("spa.billing.noOrder", { defaultValue: "No order linked yet. Create one from this appointment." })
        : error?.message || t("spa.billing.loadError", { defaultValue: "Error loading appointment order" });
      setBillingDraft((current) => ({ ...current, appointmentId: appointment.id, order: null, loading: false, error: message }));
      return null;
    }
  };

  const openPuntoCassa = async () => {
    if (!canOpenCashier || !draft.id) return;
    let order = null;
    setBillingDraft((current) => ({ ...current, appointmentId: draft.id, loading: true, error: "" }));
    try {
      const response = await createOrLinkSpaAppointmentOrder(draft.id, {}, token, tenant);
      order = normalizeOrderPayload(response);
    } catch (error) {
      setBillingDraft((current) => ({
        ...current,
        loading: false,
        error: error?.message || t("spa.billing.orderCreateError", { defaultValue: "Error creating appointment order" }),
      }));
      return;
    }
    const orderId = orderIdOf(order);
    if (!orderId) return;
    setBillingDraft((current) => ({
      ...current,
      order,
      loading: false,
      checkoutKey: appointmentCheckoutKey(tenant, draft.id),
    }));
    navigate("/punto_cassa", {
      state: {
        restoredOrderIds: [Number(orderId)],
        spaAppointmentId: Number(draft.id),
      },
    });
  };

  const createProfessionalEngagement = async () => {
    if (!canManageResources || !draft.id || !draft.operatorId) return;
    setEngagementBusy(true);
    setDialogError("");
    try {
      const invitation = await createSpaOperatorInvitation(draft.operatorId, {
        appointmentId: Number(draft.id),
        expiresInHours: Number(engagementDraft.expiresInHours || 168),
      }, token, tenant);
      setEngagementToken(invitation?.token || "");
      await loadEngagementInvitations(draft.operatorId, draft.id);
      showSnackbar(
        invitation?.emailSent === false
          ? t("spa.engagements.createdEmailFailed", { defaultValue: "Engagement created, but email was not sent." })
          : t("spa.engagements.created", { defaultValue: "Engagement invitation created." }),
        invitation?.emailSent === false ? "warning" : "success"
      );
    } catch (error) {
      setDialogError(error?.message || t("spa.engagements.createError", { defaultValue: "Could not create engagement invitation" }));
    } finally {
      setEngagementBusy(false);
    }
  };

  const resendProfessionalEngagement = async (invitationId) => {
    if (!canManageResources || !draft.id || !draft.operatorId) return;
    setEngagementBusy(true);
    setDialogError("");
    try {
      const invitation = await resendSpaOperatorInvitation(draft.operatorId, invitationId, { expiresInHours: Number(engagementDraft.expiresInHours || 168) }, token, tenant);
      setEngagementToken(invitation?.token || "");
      await loadEngagementInvitations(draft.operatorId, draft.id);
      showSnackbar(
        invitation?.emailSent === false
          ? t("spa.engagements.resentEmailFailed", { defaultValue: "Engagement resent, but email was not sent." })
          : t("spa.engagements.resent", { defaultValue: "Engagement invitation resent." }),
        invitation?.emailSent === false ? "warning" : "success"
      );
    } catch (error) {
      setDialogError(error?.message || t("spa.engagements.resendError", { defaultValue: "Could not resend engagement invitation" }));
    } finally {
      setEngagementBusy(false);
    }
  };

  const revokeProfessionalEngagement = async (invitationId) => {
    if (!canManageResources || !draft.id || !draft.operatorId) return;
    setEngagementBusy(true);
    setDialogError("");
    try {
      await revokeSpaOperatorInvitation(draft.operatorId, invitationId, token, tenant);
      setEngagementToken("");
      await loadEngagementInvitations(draft.operatorId, draft.id);
      showSnackbar(t("spa.engagements.revoked", { defaultValue: "Engagement invitation revoked." }), "success");
    } catch (error) {
      setDialogError(error?.message || t("spa.engagements.revokeError", { defaultValue: "Could not revoke engagement invitation" }));
    } finally {
      setEngagementBusy(false);
    }
  };

  const copyEngagementUrl = async () => {
    if (!engagementUrl) return;
    try {
      await navigator.clipboard.writeText(engagementUrl);
      showSnackbar(t("spa.engagements.urlCopied", { defaultValue: "Engagement URL copied." }), "success");
    } catch (_) {
      setDialogError(t("spa.engagements.urlCopyError", { defaultValue: "Could not copy engagement URL" }));
    }
  };

  const updateCheckoutPayment = (index, patch) => {
    setBillingDraft((current) => ({
      ...current,
      payments: current.payments.map((item, itemIndex) => {
        if (itemIndex !== index) return item;
        const next = { ...item, ...patch };
        if (patch.method === "ROOM_CHARGE") next.isFiscal = false;
        if (patch.method && patch.method !== "ROOM_CHARGE" && item.method === "ROOM_CHARGE") next.isFiscal = true;
        return next;
      }),
    }));
  };

  const addCheckoutPayment = () => {
    setBillingDraft((current) => ({
      ...current,
      payments: [...current.payments, { method: "CARD", amount: "", isFiscal: true, note: "" }],
    }));
  };

  const removeCheckoutPayment = (index) => {
    setBillingDraft((current) => ({
      ...current,
      payments: current.payments.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const updateStoredValueRow = (index, patch) => {
    setBillingDraft((current) => ({
      ...current,
      storedValue: current.storedValue.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)),
    }));
  };

  const addStoredValueRow = () => {
    setBillingDraft((current) => ({
      ...current,
      storedValue: [...current.storedValue, { instrumentId: "", amount: "" }],
    }));
  };

  const removeStoredValueRow = (index) => {
    setBillingDraft((current) => ({
      ...current,
      storedValue: current.storedValue.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const searchPmsReservations = async (overrides = {}) => {
    setPmsDraft((current) => ({ ...current, loading: true, error: "" }));
    try {
      const response = await searchSpaPmsReservations({
        search: overrides.search ?? pmsDraft.search,
        from: overrides.from ?? pmsDraft.from ?? draft.date,
        to: overrides.to ?? pmsDraft.to ?? addDays(draft.date, 7),
      }, token, tenant);
      setPmsDraft((current) => ({
        ...current,
        search: overrides.search ?? current.search,
        from: overrides.from ?? current.from,
        to: overrides.to ?? current.to,
        loading: false,
        results: normalizeArrayPayload(response),
      }));
    } catch (error) {
      setPmsDraft((current) => ({
        ...current,
        loading: false,
        error: error?.message || t("spa.pms.searchError", { defaultValue: "Error searching PMS reservations" }),
      }));
    }
  };

  const openHelitePicker = () => {
    const from = pmsDraft.from || draft.date || selectedDate;
    const to = !pmsDraft.to || pmsDraft.to <= from ? addDays(from, 7) : pmsDraft.to;
    setHelitePickerOpen(true);
    setPmsDraft((current) => ({ ...current, from, to, error: "" }));
    searchPmsReservations({ from, to, search: pmsDraft.search });
  };

  const linkPmsReservation = async (reservation) => {
    if (!canManageAppointments) return;
    const reservationId = reservationIdOf(reservation);
    const billId = billIdOf(reservation);
    const guestId = guestIdOf(reservation);
    if (!reservationId || !billId || !guestId) {
      setPmsDraft((current) => ({
        ...current,
        error: t("spa.pms.missingIdentifiers", { defaultValue: "Reservation, bill and guest identifiers are required." }),
      }));
      return;
    }
    const selectedClientId = Number(reservation.clientId || reservation.customer?.id || reservation.guest?.clientId || 0);
    const selectedGuestName = pmsGuestNameOf(reservation);
    setDraft((current) => ({
      ...current,
      pmsReservationId: reservationId,
      pmsBillId: billId,
      pmsGuestId: guestId,
      roomNumber: pmsRoomNumberOf(reservation) || current.roomNumber,
      clientId: selectedClientId ? String(selectedClientId) : current.clientId,
      clientName: selectedClientId || current.clientId ? current.clientName : selectedGuestName || current.clientName,
    }));
    if (!draft.id) {
      setPmsDraft((current) => ({ ...current, results: [], error: "" }));
      setHelitePickerOpen(false);
      showSnackbar(t("spa.pms.selectedForCreate", { defaultValue: "Hotel reservation selected. It will be linked when you save." }), "success");
      return;
    }
    setPmsDraft((current) => ({ ...current, linking: true, error: "" }));
    try {
      const updated = await linkSpaAppointmentPmsReservation(draft.id, {
        reservationId,
        billId,
        guestId,
      }, token, tenant);
      updateAppointmentInState(updated);
      mergeAppointmentIntoDraft(updated);
      await loadPlannerData();
      setPmsDraft((current) => ({ ...current, linking: false, results: [] }));
      setHelitePickerOpen(false);
      showSnackbar(t("spa.pms.linked", { defaultValue: "PMS reservation linked" }), "success");
    } catch (error) {
      setPmsDraft((current) => ({
        ...current,
        linking: false,
        error: error?.message || t("spa.pms.linkError", { defaultValue: "Error linking PMS reservation" }),
      }));
    }
  };

  const loadPmsCharges = async () => {
    if (!draft.id) return;
    setPmsDraft((current) => ({ ...current, chargesLoading: true, error: "" }));
    try {
      const charges = await getSpaAppointmentPmsCharges(draft.id, token, tenant);
      setPmsDraft((current) => ({ ...current, chargesLoading: false, charges }));
    } catch (error) {
      setPmsDraft((current) => ({
        ...current,
        chargesLoading: false,
        error: error?.message || t("spa.pms.chargesError", { defaultValue: "Error loading PMS charges" }),
      }));
    }
  };

  const submitSpaCheckout = async () => {
    if (!canManageFinance || !draft.id || !billingDraft.order) return;
    setBillingDraft((current) => ({ ...current, checkoutSubmitting: true, error: "" }));
    try {
      await checkoutSpaAppointment(draft.id, {
        payments: billingDraft.payments
          .filter((item) => Number(item.amount || 0) > 0)
          .map((item) => ({
            method: item.method,
            amount: Number(item.amount || 0),
            isFiscal: Boolean(item.isFiscal),
            ...(item.note ? { note: item.note } : {}),
          })),
        storedValue: billingDraft.storedValue
          .filter((item) => item.instrumentId && Number(item.amount || 0) > 0)
          .map((item) => ({ instrumentId: Number(item.instrumentId), amount: Number(item.amount || 0) })),
        discount: Number(billingDraft.discount || 0),
        discountAmount: Number(billingDraft.discountAmount || 0),
        idempotencyKey: billingDraft.checkoutKey || appointmentCheckoutKey(tenant, draft.id),
      }, token, tenant);
      await loadAppointmentOrder(draft);
      await loadPlannerData();
      setBillingDraft((current) => ({ ...current, checkoutOpen: false, checkoutSubmitting: false }));
      showSnackbar(t("spa.billing.checkoutSaved", { defaultValue: "SPA checkout completed" }), "success");
    } catch (error) {
      if (billingDraft.payments.some((item) => item.method === "ROOM_CHARGE")) {
        await loadPmsCharges();
      }
      const details = error?.payload?.remaining !== undefined
        ? ` ${t("spa.billing.remaining", { defaultValue: "Remaining" })}: ${money(error.payload.remaining)}`
        : "";
      setBillingDraft((current) => ({
        ...current,
        checkoutSubmitting: false,
        error: `${error?.message || t("spa.billing.checkoutError", { defaultValue: "Error completing checkout" })}${details}`,
      }));
    }
  };

  const openSettingsDialog = () => {
    if (!canViewSettings) return;
    setSettingsDraft(normalizeSchedulingSettings(schedulingSettings));
    setSettingsDialogOpen(true);
  };

  const updateOpeningHour = (index, patch) => {
    setSettingsDraft((current) => ({
      ...current,
      openingHours: current.openingHours.map((item, itemIndex) => (
        itemIndex === index ? { ...item, ...patch } : item
      )),
    }));
  };

  const removeOpeningHour = (index) => {
    setSettingsDraft((current) => ({
      ...current,
      openingHours: current.openingHours.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const addOpeningHour = () => {
    setSettingsDraft((current) => ({
      ...current,
      openingHours: [...current.openingHours, { dayOfWeek: 1, start: "09:00", end: "18:00" }],
    }));
  };

  const toggleClosedDay = (day) => {
    setSettingsDraft((current) => ({
      ...current,
      closedDays: current.closedDays.includes(day)
        ? current.closedDays.filter((item) => item !== day)
        : [...current.closedDays, day].sort((a, b) => a - b),
    }));
  };

  const updateHoliday = (index, patch) => {
    setSettingsDraft((current) => ({
      ...current,
      holidays: current.holidays.map((item, itemIndex) => (
        itemIndex === index ? { ...item, ...patch } : item
      )),
    }));
  };

  const removeHoliday = (index) => {
    setSettingsDraft((current) => ({
      ...current,
      holidays: current.holidays.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const addHoliday = () => {
    setSettingsDraft((current) => ({
      ...current,
      holidays: [...current.holidays, { date: selectedDate, name: "" }],
    }));
  };

  const saveSchedulingSettings = async () => {
    if (!canManageSettings || settingsSaving) return;
    setSettingsError("");
    const cancellationPolicyError = validateCancellationPolicyDraft(cancellationPolicyDraft);
    if (cancellationPolicyError) {
      setSettingsError(t(`spa.settings.policyErrors.${cancellationPolicyError}`, {
        defaultValue: "Check the cancellation policy rules and intervals.",
      }));
      return;
    }
    setSettingsSaving(true);
    const cancellationPolicyPayload = buildCancellationPolicyPayload(cancellationPolicyDraft);
    const payload = {
      openingHours: settingsDraft.openingHours.map((item) => ({
        dayOfWeek: Number(item.dayOfWeek),
        start: item.start,
        end: item.end,
      })),
      closedDays: settingsDraft.closedDays.map(Number),
      holidays: settingsDraft.holidays
        .filter((item) => item.date)
        .map((item) => ({
          date: item.date,
          ...(String(item.name || "").trim() ? { name: String(item.name).trim() } : {}),
        })),
      plannerInterval: Number(settingsDraft.plannerInterval),
    };
    try {
      const saved = await updateSpaSchedulingSettings(payload, token, tenant);
      if (cancellationPolicyPayload) {
        const policy = await updateSpaCancellationPolicy(cancellationPolicyPayload, token, tenant);
        const normalizedPolicy = policy || cancellationPolicyPayload;
        setCancellationPolicyDraft(cancellationPolicyToDraft(normalizedPolicy));
      }
      const normalized = normalizeSchedulingSettings(saved);
      setSchedulingSettings(normalized);
      setSettingsDraft(normalized);
      setSettingsDialogOpen(false);
    } catch (error) {
      setSettingsError(error?.message || t("spa.settings.saveError"));
    } finally {
      setSettingsSaving(false);
    }
  };

  const addPlannerNote = async () => {
    const note = noteDraft.trim();
    if (!canManageAppointments || !note || noteSaving) return;
    setNoteSaving(true);
    try {
      await createSpaPlannerNote({ date: selectedDate, note }, token, tenant);
      setNoteDraft("");
      await loadPlannerData();
      showSnackbar(t("spa.planner.noteSaved", { defaultValue: "Nota salvata" }), "success");
    } catch (error) {
      showSnackbar(error?.message || t("spa.planner.noteSaveError", { defaultValue: "Errore salvataggio nota" }), "error");
    } finally {
      setNoteSaving(false);
    }
  };

  const removePlannerNote = async (noteId) => {
    if (!canManageAppointments || noteSaving) return;
    setNoteSaving(true);
    try {
      await deleteSpaPlannerNote(noteId, token, tenant);
      await loadPlannerData();
      showSnackbar(t("spa.planner.noteDeleted", { defaultValue: "Nota eliminata" }), "success");
    } catch (error) {
      showSnackbar(error?.message || t("spa.planner.noteDeleteError", { defaultValue: "Errore eliminazione nota" }), "error");
    } finally {
      setNoteSaving(false);
    }
  };

  const pageSurface = {
    background: isDark ? "#151515" : "#FFFFFF",
    borderColor: isDark ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.12)",
  };

  const mutedText = isDark ? "rgba(255,255,255,0.68)" : "text.secondary";

  const statusLabel = (item) => t(`spa.statuses.${item.value}`, { defaultValue: item.label });
  const dayLabel = (day) => t(`spa.settings.days.${day}`);

  if (tenantType !== "SPA") {
    return (
      <SidebarLayout className="admin-responsive-page">
        <Container sx={{ py: 4 }}>
          <Alert severity="warning">{t("spa.common.spaOnly")}</Alert>
        </Container>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout className="admin-responsive-page">
      <Container maxWidth={false} sx={{ py: { xs: 2, md: 3 }, px: { xs: 1.5, md: 4 }, maxWidth: 1680 }}>
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 1.5, md: 2 },
            mb: 2,
            ...pageSurface,
          }}
        >
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} spacing={2}>
            <Box sx={{ minWidth: 220 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SpaIcon sx={{ color: "#D47200" }} />
                <Typography variant="h5" fontWeight={900}>{t("spa.planner.title")}</Typography>
              </Stack>
              <Typography sx={{ color: mutedText, fontSize: 14 }}>
                {t("spa.planner.subtitle")}
              </Typography>
            </Box>
            <Stack direction={{ xs: "column", lg: "row" }} spacing={1} alignItems={{ xs: "stretch", lg: "center" }}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Button
                  variant="outlined"
                  aria-label="Previous date"
                  onClick={() => setSelectedDate(addDays(selectedDate, view === "week" ? -7 : -1))}
                  sx={{ minWidth: 38, px: 0 }}
                >
                  <ChevronLeftIcon />
                </Button>
                <Button variant="outlined" onClick={() => setSelectedDate(todayKey())}>
                  {t("spa.planner.today", { defaultValue: "Oggi" })}
                </Button>
                <Button
                  variant="outlined"
                  aria-label="Next date"
                  onClick={() => setSelectedDate(addDays(selectedDate, view === "week" ? 7 : 1))}
                  sx={{ minWidth: 38, px: 0 }}
                >
                  <ChevronRightIcon />
                </Button>
              </Stack>
              <Button
                variant="outlined"
                startIcon={<CalendarMonthIcon />}
                onClick={() => document.getElementById("spa-planner-date")?.showPicker?.()}
                sx={{ minWidth: 220, textTransform: "capitalize" }}
              >
                {formatLongDate(selectedDate, i18n.language)}
              </Button>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={view}
                onChange={(_, value) => value && setView(value)}
              >
                <ToggleButton value="day">{t("spa.planner.day")}</ToggleButton>
                <ToggleButton value="week">{t("spa.planner.week")}</ToggleButton>
              </ToggleButtonGroup>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog} disabled={!canManageAppointments} sx={{ minWidth: 180 }}>
                {t("spa.planner.newAppointment")}
              </Button>
              <Button
                variant="outlined"
                startIcon={<GroupsOutlinedIcon />}
                onClick={openCreateGroupDialog}
                disabled={!canManageAppointments}
                sx={{ minWidth: 210 }}
              >
                {t("spa.planner.newGroupBooking", { defaultValue: "Couple / group booking" })}
              </Button>
              <Button variant="outlined" startIcon={<SettingsSuggestOutlinedIcon />} onClick={openSettingsDialog} disabled={!canViewSettings} sx={{ minWidth: 44 }}>
                {t("spa.settings.title")}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {settingsError ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {settingsError}
          </Alert>
        ) : null}

        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, ...pageSurface }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={`${t("spa.settings.interval")}: ${schedulingSettings.plannerInterval} min`} />
              <Chip size="small" label={`${t("spa.settings.timeZone")}: ${schedulingSettings.timeZone}`} />
              {settingsLoading ? <Chip size="small" label={t("spa.settings.loading")} /> : null}
              {selectedDateClosed ? <Chip size="small" color="warning" label={t("spa.settings.closedDay")} /> : null}
              {selectedHoliday ? <Chip size="small" color="warning" label={selectedHoliday.name || selectedHoliday.date} /> : null}
            </Stack>
            <Typography variant="caption" sx={{ color: mutedText }}>
              {selectedDateOpeningHours.length
                ? selectedDateOpeningHours.map((item) => `${item.start}-${item.end}`).join(", ")
                : t("spa.settings.noOpeningHours")}
            </Typography>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, ...pageSurface }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                id="spa-planner-date"
                fullWidth
                size="small"
                type="date"
                label={t("spa.fields.date")}
                value={selectedDate}
                onChange={(event) => setSelectedDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("spa.fields.operator")}</InputLabel>
                <Select label={t("spa.fields.operator")} value={operatorId} onChange={(event) => setOperatorId(event.target.value)}>
                  <MenuItem value="">{t("spa.common.all")}</MenuItem>
                  {operators.map((operator) => <MenuItem key={operator.id} value={operator.id}>{operator.displayName || operator.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("spa.fields.cabin")}</InputLabel>
                <Select label={t("spa.fields.cabin")} value={resourceId} onChange={(event) => setResourceId(event.target.value)}>
                  <MenuItem value="">{t("spa.common.all")}</MenuItem>
                  {resources.map((resource) => <MenuItem key={resource.id} value={resource.id}>{resource.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("spa.fields.status")}</InputLabel>
                <Select label={t("spa.fields.status")} value={status} onChange={(event) => setStatus(event.target.value)}>
                  <MenuItem value="">{t("spa.common.all")}</MenuItem>
                  {SPA_STATUSES
                    .filter((item) => ["OPTION", "CONFIRMED", "GUARANTEED", "CANCELLED", "NO_SHOW"].includes(item.value))
                    .map((item) => <MenuItem key={item.value} value={item.value}>{statusLabel(item)}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<CalendarMonthIcon />}
                onClick={() => {
                  setOperatorId("");
                  setResourceId("");
                  setStatus("");
                }}
                sx={{ minHeight: 40 }}
              >
                {t("spa.common.clear")}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, ...pageSurface }}>
          <Grid container spacing={1.5} alignItems="flex-start">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                label={t("spa.planner.dailyNote", { defaultValue: "Daily operational note" })}
                value={noteDraft}
                onChange={(event) => setNoteDraft(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack spacing={1}>
                <Button
                  variant="outlined"
                  onClick={addPlannerNote}
                  disabled={!canManageAppointments || !noteDraft.trim() || noteSaving}
                  sx={{ minHeight: 40 }}
                >
                  {noteSaving ? t("spa.common.saving", { defaultValue: "Salvataggio..." }) : t("spa.planner.addNote", { defaultValue: "Add note" })}
                </Button>
                {visiblePlannerNotes.length ? (
                  <Stack spacing={0.75}>
                    {visiblePlannerNotes.map((note) => (
                      <Paper key={note.id} variant="outlined" sx={{ p: 1, background: isDark ? "rgba(255,255,255,0.04)" : "#fff" }}>
                        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                          <Box>
                            <Typography variant="caption" color="text.secondary">{note.date}</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>{note.note}</Typography>
                          </Box>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => removePlannerNote(note.id)}
                            disabled={!canManageAppointments || noteSaving}
                            sx={{ minWidth: 36 }}
                          >
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                          </Button>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                ) : null}
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {loading ? (
          <Alert severity="info">{t("spa.planner.loading")}</Alert>
        ) : view === "day" ? (
          <Paper variant="outlined" sx={{ ...pageSurface, overflow: "hidden" }}>
            <Box sx={{ width: "100%", overflow: "hidden" }}>
              <Box sx={{ width: "100%" }}>
                <Box sx={{ display: "grid", gridTemplateColumns: "clamp(128px, 13vw, 190px) minmax(0, 1fr)", borderBottom: "1px solid", borderColor: "divider" }}>
                  <Box sx={{ px: 2, py: 1.5, borderRight: "1px solid", borderColor: "divider" }}>
                    <Typography variant="overline" fontWeight={900}>{t("spa.fields.operator")}</Typography>
                  </Box>
                  <Box
                    sx={{
                      position: "relative",
                      height: 48,
                      backgroundImage: `repeating-linear-gradient(to right, transparent 0, transparent calc(${100 / ((dayTimeline.end - dayTimeline.start) / 60)}% - 1px), ${isDark ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.12)"} calc(${100 / ((dayTimeline.end - dayTimeline.start) / 60)}% - 1px), ${isDark ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.12)"} ${100 / ((dayTimeline.end - dayTimeline.start) / 60)}%)`,
                    }}
                  >
                    {dayTimeline.hours.slice(0, -1).map((minute) => (
                      <Box
                        key={minute}
                        sx={{
                          position: "absolute",
                          left: `${((minute - dayTimeline.start + 30) / (dayTimeline.end - dayTimeline.start)) * 100}%`,
                          top: 0,
                          bottom: 0,
                          px: 0.5,
                          pt: 1.25,
                          transform: "translateX(-50%)",
                        }}
                      >
                        <Typography variant="caption" fontWeight={800} sx={{ fontSize: { xs: 9, md: 11 }, whiteSpace: "nowrap" }}>
                          {minutesToTime(minute)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {visibleOperators.map((operator) => {
                  const operatorAppointments = filteredAppointments.filter((appointment) => (
                    appointment.date === selectedDate && Number(appointment.operatorId) === Number(operator.id)
                  ));
                  return (
                    <Box
                      key={operator.id}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "clamp(128px, 13vw, 190px) minmax(0, 1fr)",
                        minHeight: 116,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ px: { xs: 1, md: 2 }, borderRight: "1px solid", borderColor: "divider", minWidth: 0 }}>
                        <Avatar src={operator.imageUrl || operator.avatarUrl || ""} sx={{ width: { xs: 30, md: 38 }, height: { xs: 30, md: 38 }, bgcolor: "primary.main", fontSize: 13 }}>
                          {operatorInitials(operator)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={900} noWrap>{operator.displayName || operator.name}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {Array.isArray(operator.skills) && operator.skills.length ? operator.skills.join(", ") : t("spa.fields.operator")}
                          </Typography>
                        </Box>
                      </Stack>
                      <Box
                        onDragOver={(event) => {
                          if (canManageAppointments) {
                            event.preventDefault();
                            event.dataTransfer.dropEffect = "move";
                          }
                        }}
                        onDrop={(event) => handleTimelineDrop(event, operator.id)}
                        sx={{
                          position: "relative",
                          minHeight: 116,
                          minWidth: 0,
                          backgroundImage: `repeating-linear-gradient(to right, transparent 0, transparent calc(${100 / ((dayTimeline.end - dayTimeline.start) / 60)}% - 1px), ${isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"} calc(${100 / ((dayTimeline.end - dayTimeline.start) / 60)}% - 1px), ${isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"} ${100 / ((dayTimeline.end - dayTimeline.start) / 60)}%)`,
                          ...(draggedAppointmentId ? { backgroundColor: alpha(theme.palette.warning.main, 0.05) } : {}),
                        }}
                      >
                        {operatorAppointments.map((appointment) => {
                          const startMinute = Math.max(dayTimeline.start, timeToMinutes(appointment.startTime));
                          const endMinute = Math.min(dayTimeline.end, timeToMinutes(appointment.endTime));
                          const visualStatusValue = appointment.operationalStatus && appointment.operationalStatus !== "SCHEDULED"
                            ? appointment.operationalStatus
                            : appointment.commercialStatus;
                          const currentStatus = statusByValue.get(visualStatusValue) || SPA_STATUSES[0];
                          const resourceName = cabinNameFromAppointment(appointment)
                            || resourceById.get(Number(resolveVisibleCabinId(appointment)))?.name
                            || "-";
                          const customerLabel = appointment.customerType === "HOTEL_GUEST"
                            ? `${t("spa.fields.room")} ${appointment.roomNumber || "-"}`
                            : t("spa.customerTypes.external");
                          return (
                            <Box
                              key={appointment.id}
                              role="button"
                              tabIndex={0}
                              draggable={canManageAppointments && !saving && !["COMPLETED", "CANCELLED", "NO_SHOW"].includes(visualStatusValue)}
                              onDragStart={(event) => handleAppointmentDragStart(event, appointment)}
                              onDragEnd={() => setDraggedAppointmentId(null)}
                              onClick={() => openEditDialog(appointment)}
                              onKeyDown={(event) => event.key === "Enter" && openEditDialog(appointment)}
                              sx={{
                                position: "absolute",
                                left: `calc(${((startMinute - dayTimeline.start) / (dayTimeline.end - dayTimeline.start)) * 100}% + 4px)`,
                                width: `calc(${Math.max(((endMinute - startMinute) / (dayTimeline.end - dayTimeline.start)) * 100, 7)}% - 8px)`,
                                top: 8,
                                bottom: 8,
                                px: 1,
                                py: 0.75,
                                overflow: "hidden",
                                cursor: canManageAppointments ? "grab" : "pointer",
                                opacity: Number(draggedAppointmentId) === Number(appointment.id) ? 0.5 : 1,
                                border: "1px solid",
                                borderColor: alpha(currentStatus.color, isDark ? 0.65 : 0.25),
                                borderRadius: "6px",
                                backgroundColor: isDark ? alpha(currentStatus.color, 0.28) : currentStatus.background,
                                color: isDark ? "#fff" : "#172033",
                                boxShadow: "0 1px 2px rgba(15,23,42,0.08)",
                                "&:hover": { borderColor: currentStatus.color, boxShadow: 3, zIndex: 2 },
                                "&:focus-visible": { outline: `2px solid ${currentStatus.color}`, outlineOffset: 1 },
                              }}
                            >
                              <Typography variant="caption" fontWeight={900} noWrap>{appointment.startTime} - {appointment.endTime}</Typography>
                              <Typography variant="body2" fontWeight={900} noWrap>{appointment.treatmentName || "-"}</Typography>
                              <Typography variant="caption" display="block" noWrap>{appointment.clientName}</Typography>
                              <Typography variant="caption" display="block" noWrap>{resourceName} | {customerLabel}</Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })}

                {!visibleOperators.length ? (
                  <Box sx={{ p: 4, textAlign: "center" }}>
                    <Typography color="text.secondary">{t("spa.planner.empty")}</Typography>
                  </Box>
                ) : null}
              </Box>
            </Box>
            <Stack direction="row" spacing={2.5} flexWrap="wrap" useFlexGap sx={{ px: 2, py: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
              {SPA_STATUSES.filter((item) => ["CONFIRMED", "GUARANTEED", "OPTION", "CANCELLED"].includes(item.value)).map((item) => (
                <Stack key={item.value} direction="row" spacing={0.75} alignItems="center">
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: item.color }} />
                  <Typography variant="caption">{statusLabel(item)}</Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        ) : (
          <Grid container spacing={1.5}>
            {visibleDates.map((dateKey) => {
              const dayAppointments = filteredAppointments.filter((appointment) => appointment.date === dateKey);
              return (
                <Grid item xs={12} md={12 / 7} key={dateKey}>
                  <Paper
                    variant="outlined"
                    onDragOver={(event) => {
                      if (canManageAppointments) {
                        event.preventDefault();
                        event.dataTransfer.dropEffect = "move";
                      }
                    }}
                    onDrop={(event) => handleAppointmentDrop(event, dateKey)}
                    sx={{ p: 1, minHeight: 360, ...pageSurface }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography fontWeight={900}>{formatDayLabel(dateKey, i18n.language)}</Typography>
                      <Chip size="small" label={dayAppointments.length} />
                    </Stack>
                    <Stack spacing={0.75}>
                      {dayAppointments.map((appointment) => {
                        const visualStatusValue = appointment.operationalStatus && appointment.operationalStatus !== "SCHEDULED"
                          ? appointment.operationalStatus
                          : appointment.commercialStatus;
                        const currentStatus = statusByValue.get(visualStatusValue) || SPA_STATUSES[0];
                        return (
                          <Paper
                            key={appointment.id}
                            variant="outlined"
                            draggable={canManageAppointments && !saving}
                            onDragStart={(event) => handleAppointmentDragStart(event, appointment)}
                            onDragEnd={() => setDraggedAppointmentId(null)}
                            onClick={() => openEditDialog(appointment)}
                            sx={{
                              p: 1,
                              cursor: canManageAppointments ? "grab" : "pointer",
                              opacity: Number(draggedAppointmentId) === Number(appointment.id) ? 0.55 : 1,
                              borderColor: alpha(currentStatus.color, 0.35),
                              background: isDark ? alpha(currentStatus.color, 0.2) : currentStatus.background,
                            }}
                          >
                            <Typography variant="caption" fontWeight={900}>{appointment.startTime} - {appointment.endTime}</Typography>
                            <Typography variant="body2" fontWeight={900}>{appointment.treatmentName || "-"}</Typography>
                            <Typography variant="caption" display="block" noWrap>{appointment.clientName}</Typography>
                            <Typography variant="caption" display="block" noWrap>{operatorById.get(Number(appointment.operatorId))?.displayName || "-"}</Typography>
                          </Paper>
                        );
                      })}
                    </Stack>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
          <DialogTitle>{draft.id ? t("spa.planner.editAppointment") : t("spa.planner.newAppointment")}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ pt: 1 }}>
              {draft.bookingGroupId ? (
                <Grid item xs={12}>
                  <Alert
                    severity="info"
                    action={(
                      <Button color="inherit" size="small" onClick={() => openManageGroupDialog(draft.bookingGroupId)}>
                        {t("spa.planner.manageGroup", { defaultValue: "Manage group" })}
                      </Button>
                    )}
                  >
                    {t("spa.planner.groupMember", {
                      defaultValue: "This appointment belongs to booking group #{{groupId}}.",
                      groupId: draft.bookingGroupId,
                    })}
                  </Alert>
                </Grid>
              ) : null}
              {dialogError ? (
                <Grid item xs={12}>
                  <Alert severity="warning" sx={{ whiteSpace: "pre-line" }}>
                    <AlertTitle>{t("spa.planner.conflictTitle", { defaultValue: "Controlla disponibilita" })}</AlertTitle>
                    {dialogError}
                  </Alert>
                </Grid>
              ) : null}
              {appointmentDetailLoading ? (
                <Grid item xs={12}>
                  <Alert severity="info" icon={<CircularProgress size={18} />}>
                    {t("spa.planner.loadingAppointment", { defaultValue: "Loading appointment details..." })}
                  </Alert>
                </Grid>
              ) : null}
              <Grid item xs={12} md={6}>
                <Autocomplete
                  freeSolo
                  options={clients}
                  value={clients.find((client) => String(client.id) === String(draft.clientId)) || null}
                  inputValue={draft.clientName || ""}
                  getOptionLabel={(option) => (typeof option === "string" ? option : displayClientName(option))}
                  isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                  onInputChange={(_, value, reason) => {
                    if (reason === "reset") return;
                    setDraft((current) => ({
                      ...current,
                      clientId: "",
                      clientName: value,
                    }));
                  }}
                  onChange={(_, value) => setDraft((current) => ({
                    ...current,
                    clientId: value && typeof value !== "string" ? String(value.id) : "",
                    clientName: typeof value === "string" ? value : value ? displayClientName(value) : "",
                  }))}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth label={t("spa.fields.client")} required />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  type="number"
                  label={t("spa.fields.partySize", { defaultValue: "Persone" })}
                  value={draft.partySize}
                  onChange={(event) => setDraft((current) => ({ ...current, partySize: Math.max(1, Number(event.target.value || 1)), selectedCombination: null, equipmentSelections: current.id ? current.equipmentSelections : [] }))}
                  inputProps={{ min: 1, max: 100 }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  label={t("spa.fields.room")}
                  value={draft.roomNumber}
                  onChange={(event) => setDraft((current) => ({ ...current, roomNumber: event.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>{t("spa.fields.type")}</InputLabel>
                  <Select
                    label={t("spa.fields.type")}
                    value={draft.customerType}
                    onChange={(event) => setDraft((current) => ({ ...current, customerType: event.target.value }))}
                  >
                    <MenuItem value="HOTEL_GUEST">{t("spa.customerTypes.hotelGuest")}</MenuItem>
                    <MenuItem value="EXTERNAL_GUEST">{t("spa.customerTypes.external")}</MenuItem>
                    <MenuItem value="MEMBER">{t("spa.customerTypes.member", { defaultValue: "Member" })}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="spa-booking-treatment">{t("spa.fields.treatment")}</InputLabel>
                  <Select
                    labelId="spa-booking-treatment"
                    label={t("spa.fields.treatment")}
                    value={draft.treatmentId}
                    disabled={Boolean(draft.id)}
                    onChange={(event) => setDraft((current) => ({
                      ...current,
                      treatmentId: event.target.value,
                      addOns: [],
                      originalAddOns: current.id ? current.originalAddOns : [],
                      selectedCombination: null,
                      equipmentSelections: [],
                    }))}
                  >
                    {treatments.map((treatment) => (
                      <MenuItem key={treatment.id} value={String(treatment.id)}>
                        {treatment.name} - {treatment.durationMinutes} min
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {draft.customerType === "HOTEL_GUEST" ? (
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 1.25, minHeight: 56, display: "flex", alignItems: "center", gap: 1, justifyContent: "space-between", flexWrap: "wrap" }}>
                    {pmsLinked ? (
                      <Chip
                        icon={<HotelOutlinedIcon />}
                        label={`${draft.roomNumber ? `${t("heliteGuestPicker.roomWithNumber", { room: draft.roomNumber })} - ` : ""}${draft.clientName || draft.pmsGuestId || "HElite"}`}
                        onDelete={() => setDraft((current) => ({
                          ...current,
                          pmsReservationId: "",
                          pmsBillId: "",
                          pmsGuestId: "",
                          pmsReference: {},
                          pmsSync: "",
                        }))}
                        sx={{ maxWidth: { xs: "100%", md: "calc(100% - 150px)" }, "& .MuiChip-label": { overflowWrap: "anywhere", whiteSpace: "normal" } }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 220 }}>
                        {t("heliteGuestPicker.noLinkedGuest", { defaultValue: "No HElite guest linked" })}
                      </Typography>
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<HotelOutlinedIcon />}
                      onClick={openHelitePicker}
                      sx={{ whiteSpace: "nowrap", height: 40, width: { xs: "100%", sm: "auto" } }}
                    >
                      {t("heliteGuestPicker.selectButton", { defaultValue: "Select HElite guest" })}
                    </Button>
                  </Paper>
                </Grid>
              ) : null}
              <Grid item xs={12}>
                <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.5 }}>
                  <Stack spacing={1.25}>
                    <Typography fontWeight={850}>{t("spa.fields.addons", { defaultValue: "Add-ons" })}</Typography>
                    {!addons.length ? (
                      <Typography variant="body2" color="text.secondary">
                        {t("spa.planner.noAddOns", { defaultValue: "No add-ons configured for this treatment." })}
                      </Typography>
                    ) : addons.map((addOn) => {
                      const selection = normalizeAddOnSelections(draft.addOns)
                        .find((item) => item.addOnId === Number(addOn.id));
                      const checked = Boolean(selection);
                      const isUpgrade = addOn.type === "TREATMENT_UPGRADE";
                      return (
                        <Grid container spacing={1} alignItems="center" key={addOn.id}>
                          <Grid item xs={12} md={5}>
                            <FormControlLabel
                              control={<Checkbox checked={checked} onChange={(event) => toggleAddOn(addOn, event.target.checked)} />}
                              label={addOn.name || addOn.product?.name || `Add-on ${addOn.id}`}
                            />
                          </Grid>
                          <Grid item xs={6} md={2}>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={t(`spa.addOnTypes.${addOn.type}`, { defaultValue: addOn.type })}
                            />
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Typography variant="body2" color="text.secondary">
                              +{money(addOn.priceAdjustment ?? addOn.priceDelta)} | +{Number(addOn.durationAdjustmentMinutes ?? addOn.durationDeltaMinutes ?? 0)} min
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label={t("spa.fields.quantity", { defaultValue: "Quantity" })}
                              value={selection?.quantity || 1}
                              disabled={!checked || isUpgrade}
                              inputProps={{ min: 1, max: 100, step: 1 }}
                              onChange={(event) => updateAddOnQuantity(addOn.id, event.target.value)}
                            />
                          </Grid>
                        </Grid>
                      );
                    })}
                    <Divider />
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip size="small" label={`${t("spa.planner.basePrice", { defaultValue: "Base" })}: ${money(addOnSummary.basePrice)}`} />
                      <Chip size="small" label={`${t("spa.planner.addOnTotal", { defaultValue: "Add-ons" })}: ${money(addOnSummary.addOnTotal)}`} />
                      <Chip size="small" color="primary" label={`${t("spa.planner.finalPrice", { defaultValue: "Total" })}: ${money(addOnSummary.finalPrice)}`} />
                      <Chip size="small" color="secondary" label={`${t("spa.planner.finalDuration", { defaultValue: "Duration" })}: ${addOnSummary.finalDurationMinutes} min`} />
                    </Stack>
                  </Stack>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label={t("spa.fields.date")}
                  value={draft.date}
                  onChange={(event) => setDraft((current) => ({ ...current, date: event.target.value, selectedCombination: null, equipmentSelections: current.id ? current.equipmentSelections : [] }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="time"
                  label={t("spa.fields.start")}
                  value={draft.startTime}
                  onChange={(event) => setDraft((current) => ({ ...current, startTime: event.target.value, selectedCombination: null, equipmentSelections: current.id ? current.equipmentSelections : [] }))}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: schedulingSettings.plannerInterval * 60 }}
                />
              </Grid>
              {draft.id ? (
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="time"
                    label={t("spa.fields.end", { defaultValue: "Fine" })}
                    value={draft.endTime || resolveEndTime(draft.startTime, treatmentById.get(Number(draft.treatmentId)) || { durationMinutes: schedulingSettings.plannerInterval })}
                    onChange={(event) => setDraft((current) => ({ ...current, endTime: event.target.value, selectedCombination: null }))}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: schedulingSettings.plannerInterval * 60 }}
                  />
                </Grid>
              ) : null}
              <Grid item xs={12} md={draft.id ? 3 : 6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={availabilityLoading ? <CircularProgress size={18} /> : <EventAvailableIcon />}
                  onClick={checkAvailability}
                  disabled={!draft.treatmentId || !draft.date || !draft.startTime || !draft.partySize || availabilityLoading || smartSearching || saving}
                  sx={{ minHeight: 56 }}
                >
                  {t("spa.planner.checkAvailability", { defaultValue: "Verifica disponibilita" })}
                </Button>
              </Grid>
              {!draft.id && dialogOpen && <Grid item xs={12}>
                <SpaSmartAvailability date={draft.date} startTime={draft.startTime} treatmentId={draft.treatmentId} addOns={normalizeAddOnSelections(draft.addOns)}
                  partySize={draft.partySize} operatorId={draft.operatorId}
                  cabinId={resourceById.get(Number(draft.resourceId))?.source === 'table-map'
                    ? backendCabins.find((cabin) => sameResourceName(cabin.name, resourceById.get(Number(draft.resourceId))?.name))?.id || '' : draft.resourceId}
                  token={token} tenant={tenant}
                  settings={schedulingSettings} ready={!settingsLoading && !settingsError && canViewSettings}
                  disabled={!canManageAppointments || saving || availabilityLoading} operators={operators} cabins={resources} equipment={equipment}
                  onBusyChange={setSmartSearching} onUseSlot={({ result, combination, localStart, localEnd, timeZone }) => {
                    if (!canManageAppointments || saving) return;
                    setAvailabilityResult(null);
                    setDraft((current) => ({ ...current, date: localStart.slice(0, 10), startTime: localStart.slice(11), endTime: localEnd.slice(11),
                      operatorId: String(combination.operatorIds[0]), resourceId: String(combination.cabinIds[0]), selectedCombination: combination,
                      equipmentSelections: combination.equipment, smartSlot: { startAt: result.startAt, localStart, timeZone } }));
                  }} />
              </Grid>}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="spa-booking-operator">{t("spa.fields.operator")}</InputLabel>
                  <Select
                    labelId="spa-booking-operator"
                    label={t("spa.fields.operator")}
                    value={draft.operatorId}
                    onChange={(event) => setDraft((current) => ({ ...current, operatorId: event.target.value, selectedCombination: null }))}
                  >
                    {operators.map((operator) => <MenuItem key={operator.id} value={String(operator.id)}>{operator.displayName || operator.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="spa-booking-cabin">{t("spa.fields.cabin")}</InputLabel>
                  <Select
                    labelId="spa-booking-cabin"
                    label={t("spa.fields.cabin")}
                    value={draft.resourceId}
                    onChange={(event) => setDraft((current) => ({ ...current, resourceId: event.target.value, selectedCombination: null }))}
                  >
                    {resources.map((resource) => <MenuItem key={resource.id} value={String(resource.id)}>{resource.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="spa-appointment-status-label">{t("spa.fields.status")}</InputLabel>
                  <Select
                    labelId="spa-appointment-status-label"
                    label={t("spa.fields.status")}
                    value={draft.status}
                    onChange={(event) => setDraft((current) => ({ ...current, status: event.target.value }))}
                  >
                    {(draft.id
                      ? SPA_STATUSES.filter((item) => item.value === currentCommercialStatus || allowedNextStatuses.includes(item.value))
                      : SPA_STATUSES.filter((item) => ["OPTION", "CONFIRMED", "GUARANTEED"].includes(item.value))
                    ).map((item) => <MenuItem key={item.value} value={item.value}>{statusLabel(item)}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              {availabilityResult ? (
                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <Alert severity={availabilityResult.available ? "success" : "warning"}>
                      {availabilityResult.available
                        ? t("spa.planner.availableCombinations", {
                          defaultValue: "{{count}} combinazioni disponibili",
                          count: availabilityResult.combinations?.length || 0,
                        })
                        : t("spa.planner.noCombinations", { defaultValue: "Nessuna combinazione disponibile" })}
                    </Alert>
                    {Array.isArray(availabilityResult.combinations) && availabilityResult.combinations.length ? (
                      <FormControl fullWidth>
                        <InputLabel id="spa-booking-combination">{t("spa.planner.resourceCombination", { defaultValue: "Combinazione risorse" })}</InputLabel>
                        <Select
                          labelId="spa-booking-combination"
                          label={t("spa.planner.resourceCombination", { defaultValue: "Combinazione risorse" })}
                          value={draft.selectedCombination ? JSON.stringify(draft.selectedCombination) : ""}
                          onChange={(event) => {
                            const combination = JSON.parse(event.target.value);
                            const combinationCabinId = Number(combination.cabinIds?.[0]);
                            setDraft((current) => ({
                              ...current,
                              selectedCombination: combination,
                              operatorId: String(combination.operatorIds?.[0] || ""),
                              resourceId: resourceById.has(combinationCabinId) ? String(combinationCabinId) : current.resourceId,
                              equipmentSelections: combination.equipment || [],
                            }));
                          }}
                        >
                          {availabilityResult.combinations.map((combination, index) => (
                            <MenuItem key={`${JSON.stringify(combination)}-${index}`} value={JSON.stringify(combination)}>
                              {formatCombinationLabel(combination, operatorById, resourceById, equipmentById)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : null}
                    {Array.isArray(availabilityResult.rejections) && availabilityResult.rejections.length ? (
                      <Alert severity="info" sx={{ whiteSpace: "pre-line" }}>
                        {availabilityResult.rejections.map(rejectionLabel).join("\n")}
                      </Alert>
                    ) : null}
                  </Stack>
                </Grid>
              ) : null}
              {draft.equipmentSelections?.length ? (
                <Grid item xs={12}>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {draft.equipmentSelections.map((item) => (
                      <Chip
                        key={item.equipmentId}
                        label={`${equipmentById.get(Number(item.equipmentId))?.name || `Equipment ${item.equipmentId}`} x${item.quantity}`}
                      />
                    ))}
                  </Stack>
                </Grid>
              ) : null}
              {draft.id && draft.status !== currentCommercialStatus ? (
                <Grid item xs={12}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <TextField
                      fullWidth
                      label={t("spa.fields.reason", { defaultValue: "Motivo" })}
                      value={draft.statusReason}
                      required={statusChangeRequiresReason}
                      onChange={(event) => setDraft((current) => ({ ...current, statusReason: event.target.value }))}
                    />
                    <Button
                      variant="contained"
                      onClick={changeCommercialStatus}
                      disabled={!canManageAppointments || (cancellationFeeStatuses.has(draft.status) && !canConfirmCancellation) || !statusChangeValid || saving || finalCommercialStatuses.has(currentCommercialStatus)}
                      sx={{ minWidth: 170 }}
                    >
                      {t("spa.planner.updateStatus", { defaultValue: "Aggiorna stato" })}
                    </Button>
                  </Stack>
                </Grid>
              ) : null}
              {Array.isArray(draft.commercialStatusHistory) && draft.commercialStatusHistory.length ? (
                <Grid item xs={12}>
                  <Stack spacing={0.5}>
                    <Typography fontWeight={900}>{t("spa.planner.statusHistory", { defaultValue: "Storico stato" })}</Typography>
                    {draft.commercialStatusHistory.map((entry) => (
                      <Typography key={entry.id} variant="caption" color="text.secondary">
                        {entry.fromStatus} - {entry.toStatus}
                        {entry.reason ? ` | ${entry.reason}` : ""}
                      </Typography>
                    ))}
                  </Stack>
                </Grid>
              ) : null}
              {draft.id ? (
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={billingDraft.loading ? <CircularProgress color="inherit" size={16} /> : <ReceiptLongOutlinedIcon />}
                    onClick={openPuntoCassa}
                    disabled={!canOpenCashier || billingDraft.loading}
                  >
                    {t("spa.billing.openPuntoCassa", { defaultValue: "Punto Cassa" })}
                  </Button>
                </Grid>
              ) : null}
              {dialogOpen && draft.id ? <Grid item xs={12}>
                <SpaAppointmentCommunications key={`${tenant}:${token}:${draft.id}`} appointmentId={draft.id} token={token} tenant={tenant} />
              </Grid> : null}
              {draft.id && draft.operatorId ? (
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: alpha(theme.palette.warning.main, isDark ? 0.12 : 0.05), borderColor: alpha(theme.palette.warning.main, 0.35) }}>
                    <Stack spacing={1.25}>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between">
                        <Box>
                          <Typography fontWeight={900}>
                            {t("spa.engagements.appointmentTitle", { defaultValue: "External professional engagement" })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t("spa.engagements.appointmentHelp", { defaultValue: "Invite the assigned professional to confirm or reject this appointment." })}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          label={selectedDraftOperator?.displayName || selectedDraftOperator?.name || t("spa.fields.operator")}
                          sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
                        />
                      </Stack>
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label={t("spa.engagements.expiresInHours", { defaultValue: "Confirmation window (hours)" })}
                            value={engagementDraft.expiresInHours}
                            inputProps={{ min: 1, max: 2160 }}
                            helperText={t("spa.engagements.expiresInHoursHelp", { defaultValue: "The appointment stays reserved while awaiting a response." })}
                            onChange={(event) => setEngagementDraft((current) => ({ ...current, expiresInHours: event.target.value }))}
                          />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={engagementBusy ? <CircularProgress size={16} /> : <EmailOutlinedIcon />}
                            onClick={createProfessionalEngagement}
                            disabled={!canManageResources || engagementBusy}
                            sx={{ minHeight: 40 }}
                          >
                            {t("spa.engagements.createInvitation", { defaultValue: "Create engagement invitation" })}
                          </Button>
                        </Grid>
                      </Grid>
                      {engagementUrl ? (
                        <Alert
                          severity="success"
                          action={(
                            <Stack direction="row" spacing={1}>
                              <Button color="inherit" size="small" startIcon={<ContentCopyOutlinedIcon />} onClick={copyEngagementUrl}>
                                {t("spa.engagements.copyUrl", { defaultValue: "Copy URL" })}
                              </Button>
                              <Button
                                color="inherit"
                                size="small"
                                startIcon={<OpenInNewOutlinedIcon />}
                                href={engagementUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {t("spa.engagements.openInvitation", { defaultValue: "Open" })}
                              </Button>
                            </Stack>
                          )}
                        >
                          {t("spa.engagements.invitationUrl", { defaultValue: "Invitation URL" })}: {engagementUrl}
                        </Alert>
                      ) : null}
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>{t("spa.fields.status")}</TableCell>
                              <TableCell>{t("spa.engagements.expiresAt", { defaultValue: "Expires at" })}</TableCell>
                              <TableCell>{t("spa.engagements.respondedAt", { defaultValue: "Responded at" })}</TableCell>
                              <TableCell align="right">{t("spa.fields.actions")}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {engagementInvitations.length ? engagementInvitations.map((invitation) => (
                              <TableRow key={invitation.id}>
                                <TableCell><Chip size="small" label={invitation.status || "-"} /></TableCell>
                                <TableCell>{formatLocalDateTime(invitation.expiresAt)}</TableCell>
                                <TableCell>{formatLocalDateTime(invitation.respondedAt)}</TableCell>
                                <TableCell align="right">
                                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<ReplayOutlinedIcon />}
                                      onClick={() => resendProfessionalEngagement(invitation.id)}
                                      disabled={!canManageResources || engagementBusy || invitation.status !== "PENDING"}
                                    >
                                      {t("spa.engagements.resend", { defaultValue: "Resend" })}
                                    </Button>
                                    <Button
                                      size="small"
                                      color="error"
                                      variant="outlined"
                                      startIcon={<DeleteOutlineOutlinedIcon />}
                                      onClick={() => revokeProfessionalEngagement(invitation.id)}
                                      disabled={!canManageResources || engagementBusy || ["REVOKED", "REJECTED", "EXPIRED"].includes(invitation.status)}
                                    >
                                      {t("spa.engagements.revoke", { defaultValue: "Revoke" })}
                                    </Button>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            )) : (
                              <TableRow>
                                <TableCell colSpan={4}>
                                  <Typography variant="body2" color="text.secondary">
                                    {t("spa.engagements.noInvitations", { defaultValue: "No engagement invitations yet." })}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Stack>
                  </Paper>
                </Grid>
              ) : null}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label={t("spa.fields.participants", { defaultValue: "Partecipanti" })}
                  value={draft.participantNames}
                  helperText={t("spa.fields.participantsHelp", { defaultValue: "Un partecipante per riga" })}
                  onChange={(event) => setDraft((current) => ({ ...current, participantNames: event.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label={t("spa.fields.notes")}
                  value={draft.notes}
                  onChange={(event) => setDraft((current) => ({ ...current, notes: event.target.value }))}
                />
              </Grid>
              {draftValidationMessages.length ? (
                <Grid item xs={12}>
                  <Alert severity="info">
                    {draftValidationMessages.join(" ")}
                  </Alert>
                </Grid>
              ) : null}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} disabled={saving}>{t("spa.common.cancel")}</Button>
            <Button
              variant="contained"
              disabled={!canManageAppointments || finalCommercialStatuses.has(currentCommercialStatus) || !draftValid || Boolean(draftScheduleIssue) || saving || smartSearching || appointmentDetailLoading}
              onClick={saveDraft}
            >
              {saving ? t("spa.common.saving", { defaultValue: "Salvataggio..." }) : t("spa.common.save")}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={helitePickerOpen}
          onClose={() => setHelitePickerOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>{t("heliteGuestPicker.title", { defaultValue: "Select HElite guest" })}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1.5} sx={{ pt: 1 }}>
              {pmsDraft.error ? <Alert severity="warning">{pmsDraft.error}</Alert> : null}
              <TextField
                fullWidth
                size="small"
                value={pmsDraft.search}
                onChange={(event) => setPmsDraft((current) => ({ ...current, search: event.target.value }))}
                onKeyDown={(event) => {
                  if (event.key === "Enter") searchPmsReservations();
                }}
                placeholder={t("heliteGuestPicker.searchPlaceholder", { defaultValue: "Search by room, guest or reservation" })}
              />
              <Grid container spacing={1}>
                <Grid item xs={12} sm={5}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label={t("spa.fields.from", { defaultValue: "From" })}
                    InputLabelProps={{ shrink: true }}
                    value={pmsDraft.from}
                    onChange={(event) => setPmsDraft((current) => ({ ...current, from: event.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label={t("spa.fields.to", { defaultValue: "To" })}
                    InputLabelProps={{ shrink: true }}
                    value={pmsDraft.to}
                    onChange={(event) => setPmsDraft((current) => ({ ...current, to: event.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button fullWidth variant="outlined" onClick={() => searchPmsReservations()} disabled={pmsDraft.loading} sx={{ height: "100%" }}>
                    {t("spa.common.search", { defaultValue: "Search" })}
                  </Button>
                </Grid>
              </Grid>
              {pmsDraft.loading ? (
                <Typography color="text.secondary">{t("heliteGuestPicker.loadingShort", { defaultValue: "Loading..." })}</Typography>
              ) : pmsDraft.results.length === 0 ? (
                <Typography color="text.secondary">{t("heliteGuestPicker.empty", { defaultValue: "No HElite guests found." })}</Typography>
              ) : (
                <Box sx={{ maxHeight: 420, overflow: "auto" }}>
                  {pmsDraft.results.map((reservation, index) => (
                    <Box
                      key={`${reservationIdOf(reservation) || index}-${billIdOf(reservation)}`}
                      onClick={() => linkPmsReservation(reservation)}
                      sx={{
                        p: 1.5,
                        cursor: "pointer",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <Typography fontWeight={700}>
                        {pmsRoomNumberOf(reservation)
                          ? `${t("heliteGuestPicker.roomWithNumber", { room: pmsRoomNumberOf(reservation) })} - ${pmsGuestNameOf(reservation) || "-"}`
                          : pmsGuestNameOf(reservation) || pmsReservationLabel(reservation)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("heliteGuestPicker.bookingShort", { number: pmsReservationNumberOf(reservation), defaultValue: "Booking {{number}}" })}
                        {pmsDateRangeLabel(reservation) ? ` | ${pmsDateRangeLabel(reservation)}` : ""}
                        {billIdOf(reservation) ? ` | Bill ${billIdOf(reservation)}` : ""}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHelitePickerOpen(false)}>
              {t("confirmOperation.cancel", { defaultValue: "Cancel" })}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={cancellationFeeDraft.open}
          onClose={() => cancellationFeeDraft.saving ? null : setCancellationFeeDraft(emptyCancellationFeeDraft)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            {cancellationFeeDraft.status === "NO_SHOW"
              ? t("spa.cancellation.noShowTitle", { defaultValue: "Confirm no-show fee" })
              : t("spa.cancellation.cancelTitle", { defaultValue: "Confirm cancellation fee" })}
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ pt: 1 }}>
              {cancellationFeeDraft.loading ? (
                <Stack alignItems="center" spacing={1}>
                  <CircularProgress size={24} />
                  <Typography>{t("spa.cancellation.loading", { defaultValue: "Calculating fee..." })}</Typography>
                </Stack>
              ) : null}
              {cancellationFeeDraft.error ? <Alert severity="warning">{cancellationFeeDraft.error}</Alert> : null}
              {cancellationFeeDraft.preview ? (
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">{t("spa.cancellation.appointmentValue", { defaultValue: "Appointment value" })}</Typography>
                      <Typography fontWeight={900}>{money(cancellationFeeDraft.preview.appointmentValue || cancellationFeeDraft.preview.orderTotal)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">{t("spa.cancellation.calculatedFee", { defaultValue: "Calculated fee" })}</Typography>
                      <Typography fontWeight={900}>{money(feeAmount(cancellationFeeDraft.preview))}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">{t("spa.cancellation.noticeMinutes", { defaultValue: "Notice minutes" })}</Typography>
                      <Typography fontWeight={900}>{cancellationFeeDraft.preview.noticeMinutes ?? "-"}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">{t("spa.cancellation.appliedPercent", { defaultValue: "Applied percent" })}</Typography>
                      <Typography fontWeight={900}>{cancellationFeeDraft.preview.appliedPercent ?? cancellationFeeDraft.preview.feePercent ?? "-"}%</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              ) : null}
              <TextField
                fullWidth
                required
                multiline
                minRows={2}
                label={t("spa.fields.reason", { defaultValue: "Reason" })}
                value={cancellationFeeDraft.reason}
                onChange={(event) => setCancellationFeeDraft((current) => ({ ...current, reason: event.target.value }))}
              />
              <TextField
                fullWidth
                type="number"
                label={t("spa.cancellation.finalAmount", { defaultValue: "Final fee amount" })}
                value={cancellationFeeDraft.finalAmount}
                onChange={(event) => setCancellationFeeDraft((current) => ({ ...current, finalAmount: event.target.value }))}
                inputProps={{ min: 0, step: "0.01" }}
              />
              <TextField
                fullWidth
                label={t("spa.cancellation.adjustmentReason", { defaultValue: "Adjustment reason" })}
                value={cancellationFeeDraft.adjustmentReason}
                onChange={(event) => setCancellationFeeDraft((current) => ({ ...current, adjustmentReason: event.target.value }))}
                helperText={t("spa.cancellation.managerHint", { defaultValue: "Required when changing or waiving the calculated fee." })}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancellationFeeDraft(emptyCancellationFeeDraft)} disabled={cancellationFeeDraft.saving}>
              {t("spa.common.cancel")}
            </Button>
            <Button
              variant="contained"
              color={cancellationFeeDraft.status === "NO_SHOW" ? "error" : "warning"}
              onClick={confirmCancellationFee}
              disabled={!canConfirmCancellation || !cancellationFeeDraft.preview || cancellationFeeDraft.loading || cancellationFeeDraft.saving || !cancellationFeeDraft.reason.trim()}
            >
              {cancellationFeeDraft.saving ? t("spa.common.saving", { defaultValue: "Saving..." }) : t("spa.common.confirm", { defaultValue: "Confirm" })}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={billingDraft.checkoutOpen}
          onClose={() => billingDraft.checkoutSubmitting ? null : setBillingDraft((current) => ({ ...current, checkoutOpen: false }))}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>{t("spa.billing.checkoutTitle", { defaultValue: "SPA checkout" })}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ pt: 1 }}>
              {billingDraft.error ? <Alert severity="warning">{billingDraft.error}</Alert> : null}
              <Alert severity="info">
                {t("spa.billing.checkoutHint", { defaultValue: "Reserved package credits are applied automatically. Add stored value and split the remaining direct payments." })}
              </Alert>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={`${t("spa.billing.orderTotal", { defaultValue: "Order total" })}: ${money(checkoutOrderTotal)}`} />
                <Chip
                  color={Math.abs(checkoutSubmittedTotal - checkoutOrderTotal) < 0.01 ? "success" : "warning"}
                  label={`${t("spa.billing.submitted", { defaultValue: "Submitted" })}: ${money(checkoutSubmittedTotal)}`}
                />
              </Stack>
              <Divider />
              <Box>
                <Typography fontWeight={900} mb={1}>{t("spa.billing.storedValue", { defaultValue: "Stored value" })}</Typography>
                <Stack spacing={1}>
                  {billingDraft.storedValue.map((row, index) => (
                    <Grid container spacing={1} key={`stored-${index}`} alignItems="center">
                      <Grid item xs={12} md={7}>
                        <FormControl fullWidth size="small">
                          <InputLabel>{t("spa.billing.instrument", { defaultValue: "Instrument" })}</InputLabel>
                          <Select
                            label={t("spa.billing.instrument", { defaultValue: "Instrument" })}
                            value={row.instrumentId}
                            onChange={(event) => updateStoredValueRow(index, { instrumentId: event.target.value })}
                          >
                            {billingDraft.storedValueInstruments.map((instrument) => (
                              <MenuItem key={instrument.id} value={instrument.id}>
                                {instrument.code || `#${instrument.id}`} - {money(instrument.availableValue ?? instrument.balance ?? 0)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={8} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label={t("spa.billing.amount", { defaultValue: "Amount" })}
                          value={row.amount}
                          onChange={(event) => updateStoredValueRow(index, { amount: event.target.value })}
                          inputProps={{ min: 0, step: "0.01" }}
                        />
                      </Grid>
                      <Grid item xs={4} md={2}>
                        <Button fullWidth color="error" onClick={() => removeStoredValueRow(index)}>
                          {t("spa.common.remove", { defaultValue: "Remove" })}
                        </Button>
                      </Grid>
                    </Grid>
                  ))}
                  <Button variant="outlined" onClick={addStoredValueRow} disabled={!billingDraft.storedValueInstruments.length}>
                    {t("spa.billing.addStoredValue", { defaultValue: "Add stored value" })}
                  </Button>
                </Stack>
              </Box>
              <Divider />
              <Box>
                <Typography fontWeight={900} mb={1}>{t("spa.billing.directPayments", { defaultValue: "Direct payments" })}</Typography>
                <Stack spacing={1}>
                  {billingDraft.payments.map((row, index) => (
                    <Grid container spacing={1} key={`payment-${index}`} alignItems="center">
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>{t("spa.billing.method", { defaultValue: "Method" })}</InputLabel>
                          <Select
                            label={t("spa.billing.method", { defaultValue: "Method" })}
                            value={row.method}
                            onChange={(event) => updateCheckoutPayment(index, { method: event.target.value })}
                          >
                            {SPA_DIRECT_PAYMENT_METHODS.map((method) => (
                              <MenuItem key={method} value={method}>{method}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label={t("spa.billing.amount", { defaultValue: "Amount" })}
                          value={row.amount}
                          onChange={(event) => updateCheckoutPayment(index, { amount: event.target.value })}
                          inputProps={{ min: 0, step: "0.01" }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t("spa.billing.note", { defaultValue: "Note" })}
                          value={row.note}
                          onChange={(event) => updateCheckoutPayment(index, { note: event.target.value })}
                        />
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={Boolean(row.isFiscal)}
                              onChange={(event) => updateCheckoutPayment(index, { isFiscal: event.target.checked })}
                              disabled={row.method === "ROOM_CHARGE"}
                            />
                          )}
                          label={row.method === "ROOM_CHARGE"
                            ? t("spa.billing.nonFiscalRoomCharge", { defaultValue: "Non-fiscal room charge" })
                            : t("spa.billing.fiscal", { defaultValue: "Fiscal" })}
                        />
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Button fullWidth color="error" disabled={billingDraft.payments.length === 1} onClick={() => removeCheckoutPayment(index)}>
                          {t("spa.common.remove", { defaultValue: "Remove" })}
                        </Button>
                      </Grid>
                    </Grid>
                  ))}
                  <Button variant="outlined" onClick={addCheckoutPayment}>
                    {t("spa.billing.addPayment", { defaultValue: "Add payment" })}
                  </Button>
                </Stack>
              </Box>
              <Grid container spacing={1}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label={t("spa.billing.discountPercent", { defaultValue: "Discount %" })}
                    value={billingDraft.discount}
                    onChange={(event) => setBillingDraft((current) => ({ ...current, discount: event.target.value }))}
                    inputProps={{ min: 0, max: 100, step: "0.01" }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label={t("spa.billing.discountAmount", { defaultValue: "Discount amount" })}
                    value={billingDraft.discountAmount}
                    onChange={(event) => setBillingDraft((current) => ({ ...current, discountAmount: event.target.value }))}
                    inputProps={{ min: 0, step: "0.01" }}
                  />
                </Grid>
              </Grid>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBillingDraft((current) => ({ ...current, checkoutOpen: false }))} disabled={billingDraft.checkoutSubmitting}>
              {t("spa.common.cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={submitSpaCheckout}
              disabled={!canManageFinance || billingDraft.checkoutSubmitting || !checkoutPaymentsValid}
            >
              {billingDraft.checkoutSubmitting ? t("spa.common.saving", { defaultValue: "Saving..." }) : t("spa.billing.submitCheckout", { defaultValue: "Submit checkout" })}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={settingsDialogOpen} onClose={() => setSettingsDialogOpen(false)} fullWidth maxWidth="lg">
          <DialogTitle>{t("spa.settings.title")}</DialogTitle>
          <DialogContent dividers>
            {!canManageSettings && <Alert severity="info" sx={{ mb: 2 }}>{t("spa.common.readOnly")}</Alert>}
            {settingsError ? <Alert severity="error" sx={{ mb: 2 }}>{settingsError}</Alert> : null}
            <Stack spacing={2}>
              <FormControl disabled={!canManageSettings} fullWidth>
                <InputLabel id="spa-planner-interval-label">{t("spa.settings.interval")}</InputLabel>
                <Select disabled={!canManageSettings}
                  labelId="spa-planner-interval-label"
                  label={t("spa.settings.interval")}
                  value={settingsDraft.plannerInterval}
                  onChange={(event) => setSettingsDraft((current) => ({ ...current, plannerInterval: Number(event.target.value) }))}
                >
                  <MenuItem value={15}>15 min</MenuItem>
                  <MenuItem value={30}>30 min</MenuItem>
                </Select>
              </FormControl>

              <SpaCancellationPolicyEditor
                value={cancellationPolicyDraft}
                onChange={setCancellationPolicyDraft}
                treatments={treatments}
                packages={packages}
                disabled={!canManageSettings}
              />

              <Box>
                <Typography fontWeight={900} mb={1}>{t("spa.settings.openingHours")}</Typography>
                <Stack spacing={1}>
                  {settingsDraft.openingHours.map((item, index) => (
                    <Grid container spacing={1} alignItems="center" key={`${item.dayOfWeek}-${index}`}>
                      <Grid item xs={12} md={4}>
                        <FormControl disabled={!canManageSettings} fullWidth size="small">
                          <InputLabel>{t("spa.settings.day")}</InputLabel>
                          <Select disabled={!canManageSettings}
                            label={t("spa.settings.day")}
                            value={item.dayOfWeek}
                            onChange={(event) => updateOpeningHour(index, { dayOfWeek: Number(event.target.value) })}
                          >
                            {DAYS.map((day) => <MenuItem key={day} value={day}>{dayLabel(day)}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={5} md={3}>
                        <TextField disabled={!canManageSettings}
                          fullWidth
                          size="small"
                          type="time"
                          label={t("spa.settings.start")}
                          value={item.start}
                          onChange={(event) => updateOpeningHour(index, { start: event.target.value })}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: settingsDraft.plannerInterval * 60 }}
                        />
                      </Grid>
                      <Grid item xs={5} md={3}>
                        <TextField disabled={!canManageSettings}
                          fullWidth
                          size="small"
                          type="time"
                          label={t("spa.settings.end")}
                          value={item.end}
                          onChange={(event) => updateOpeningHour(index, { end: event.target.value })}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: settingsDraft.plannerInterval * 60 }}
                        />
                      </Grid>
                      <Grid item xs={2} md={2}>
                        <Button disabled={!canManageSettings} color="error" onClick={() => removeOpeningHour(index)}><DeleteOutlineOutlinedIcon /></Button>
                      </Grid>
                    </Grid>
                  ))}
                </Stack>
                <Button disabled={!canManageSettings} sx={{ mt: 1 }} startIcon={<AddIcon />} onClick={addOpeningHour}>{t("spa.settings.addOpeningHour")}</Button>
              </Box>

              <Box>
                <Typography fontWeight={900} mb={1}>{t("spa.settings.closedDays")}</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {DAYS.map((day) => (
                    <Chip disabled={!canManageSettings}
                      key={day}
                      label={dayLabel(day)}
                      color={settingsDraft.closedDays.includes(day) ? "warning" : "default"}
                      variant={settingsDraft.closedDays.includes(day) ? "filled" : "outlined"}
                      onClick={() => toggleClosedDay(day)}
                    />
                  ))}
                </Stack>
              </Box>

              <Box>
                <Typography fontWeight={900} mb={1}>{t("spa.settings.holidays")}</Typography>
                <Stack spacing={1}>
                  {settingsDraft.holidays.map((item, index) => (
                    <Grid container spacing={1} alignItems="center" key={`${item.date}-${index}`}>
                      <Grid item xs={12} md={4}>
                        <TextField disabled={!canManageSettings}
                          fullWidth
                          size="small"
                          type="date"
                          label={t("spa.settings.holidayDate")}
                          value={item.date}
                          onChange={(event) => updateHoliday(index, { date: event.target.value })}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={10} md={6}>
                        <TextField disabled={!canManageSettings}
                          fullWidth
                          size="small"
                          label={t("spa.settings.holidayName")}
                          value={item.name || ""}
                          onChange={(event) => updateHoliday(index, { name: event.target.value })}
                        />
                      </Grid>
                      <Grid item xs={2} md={2}>
                        <Button disabled={!canManageSettings} color="error" onClick={() => removeHoliday(index)}><DeleteOutlineOutlinedIcon /></Button>
                      </Grid>
                    </Grid>
                  ))}
                </Stack>
                <Button disabled={!canManageSettings} sx={{ mt: 1 }} startIcon={<AddIcon />} onClick={addHoliday}>{t("spa.settings.addHoliday")}</Button>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSettingsDialogOpen(false)} disabled={settingsSaving}>{t("spa.common.cancel")}</Button>
            <Button variant="contained" onClick={saveSchedulingSettings} disabled={!canManageSettings || settingsSaving}>
              {settingsSaving ? t("spa.settings.saving") : t("spa.common.save")}
            </Button>
          </DialogActions>
        </Dialog>

        <SpaGroupBookingDialog
          open={groupDialog.open}
          groupId={groupDialog.groupId}
          initialDate={selectedDate}
          clients={clients}
          treatments={treatments}
          operators={operators}
          cabins={resources}
          plannerInterval={schedulingSettings.plannerInterval}
          token={token}
          tenant={tenant}
          canManageFinance={canManageFinance}
          canManageAppointments={canManageAppointments}
          ensureCabinId={ensureBackendCabinId}
          onClose={() => setGroupDialog({ open: false, groupId: null })}
          onSaved={loadPlannerData}
          showSnackbar={showSnackbar}
        />

      </Container>
    </SidebarLayout>
  );
}

export default SpaPlannerPage;
